import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { M as MediaCardContainer } from './media-card-container.js';
import { a as extractElementsFromSlot, e as extractElementFromSlot } from './slot-utils.js';
import { c as createCommonjsModule } from './_commonjsHelpers.js';
import { d as defineCustomElement$3 } from './format-duration.js';
import { d as defineCustomElement$2 } from './icon.js';

function toTitleCase(text) {
  if (typeof text !== 'string')
    return text;
  const words = text.split(' ');
  return words
    .map((word) => {
    if (word.length === 0)
      return '';
    return word[0].toUpperCase() + word.slice(1);
  })
    .join(' ');
}

var humanizeDuration = createCommonjsModule(function (module) {
// HumanizeDuration.js - https://git.io/j0HgmQ

/* global define, module */

(function () {
  // This has to be defined separately because of a bug: we want to alias
  // `gr` and `el` for backwards-compatiblity. In a breaking change, we can
  // remove `gr` entirely.
  // See https://github.com/EvanHahn/HumanizeDuration.js/issues/143 for more.
  var greek = {
    y: function (c) {
      return c === 1 ? "χρόνος" : "χρόνια";
    },
    mo: function (c) {
      return c === 1 ? "μήνας" : "μήνες";
    },
    w: function (c) {
      return c === 1 ? "εβδομάδα" : "εβδομάδες";
    },
    d: function (c) {
      return c === 1 ? "μέρα" : "μέρες";
    },
    h: function (c) {
      return c === 1 ? "ώρα" : "ώρες";
    },
    m: function (c) {
      return c === 1 ? "λεπτό" : "λεπτά";
    },
    s: function (c) {
      return c === 1 ? "δευτερόλεπτο" : "δευτερόλεπτα";
    },
    ms: function (c) {
      return (c === 1 ? "χιλιοστό" : "χιλιοστά") + " του δευτερολέπτου";
    },
    decimal: ","
  };

  var ARABIC_DIGITS = ["۰", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

  var LANGUAGES = {
    af: {
      y: "jaar",
      mo: function (c) {
        return "maand" + (c === 1 ? "" : "e");
      },
      w: function (c) {
        return c === 1 ? "week" : "weke";
      },
      d: function (c) {
        return c === 1 ? "dag" : "dae";
      },
      h: function (c) {
        return c === 1 ? "uur" : "ure";
      },
      m: function (c) {
        return c === 1 ? "minuut" : "minute";
      },
      s: function (c) {
        return "sekonde" + (c === 1 ? "" : "s");
      },
      ms: function (c) {
        return "millisekonde" + (c === 1 ? "" : "s");
      },
      decimal: ","
    },
    ar: {
      y: function (c) {
        return ["سنة", "سنتان", "سنوات"][getArabicForm(c)];
      },
      mo: function (c) {
        return ["شهر", "شهران", "أشهر"][getArabicForm(c)];
      },
      w: function (c) {
        return ["أسبوع", "أسبوعين", "أسابيع"][getArabicForm(c)];
      },
      d: function (c) {
        return ["يوم", "يومين", "أيام"][getArabicForm(c)];
      },
      h: function (c) {
        return ["ساعة", "ساعتين", "ساعات"][getArabicForm(c)];
      },
      m: function (c) {
        return ["دقيقة", "دقيقتان", "دقائق"][getArabicForm(c)];
      },
      s: function (c) {
        return ["ثانية", "ثانيتان", "ثواني"][getArabicForm(c)];
      },
      ms: function (c) {
        return ["جزء من الثانية", "جزآن من الثانية", "أجزاء من الثانية"][
          getArabicForm(c)
        ];
      },
      decimal: ",",
      delimiter: " و ",
      _formatCount: function (count, decimal) {
        var replacements = assign(ARABIC_DIGITS, { ".": decimal });
        var characters = count.toString().split("");
        for (var i = 0; i < characters.length; i++) {
          var character = characters[i];
          if (has(replacements, character)) {
            characters[i] = replacements[character];
          }
        }
        return characters.join("");
      }
    },
    bg: {
      y: function (c) {
        return ["години", "година", "години"][getSlavicForm(c)];
      },
      mo: function (c) {
        return ["месеца", "месец", "месеца"][getSlavicForm(c)];
      },
      w: function (c) {
        return ["седмици", "седмица", "седмици"][getSlavicForm(c)];
      },
      d: function (c) {
        return ["дни", "ден", "дни"][getSlavicForm(c)];
      },
      h: function (c) {
        return ["часа", "час", "часа"][getSlavicForm(c)];
      },
      m: function (c) {
        return ["минути", "минута", "минути"][getSlavicForm(c)];
      },
      s: function (c) {
        return ["секунди", "секунда", "секунди"][getSlavicForm(c)];
      },
      ms: function (c) {
        return ["милисекунди", "милисекунда", "милисекунди"][getSlavicForm(c)];
      },
      decimal: ","
    },
    bn: {
      y: "বছর",
      mo: "মাস",
      w: "সপ্তাহ",
      d: "দিন",
      h: "ঘন্টা",
      m: "মিনিট",
      s: "সেকেন্ড",
      ms: "মিলিসেকেন্ড"
    },
    ca: {
      y: function (c) {
        return "any" + (c === 1 ? "" : "s");
      },
      mo: function (c) {
        return "mes" + (c === 1 ? "" : "os");
      },
      w: function (c) {
        return "setman" + (c === 1 ? "a" : "es");
      },
      d: function (c) {
        return "di" + (c === 1 ? "a" : "es");
      },
      h: function (c) {
        return "hor" + (c === 1 ? "a" : "es");
      },
      m: function (c) {
        return "minut" + (c === 1 ? "" : "s");
      },
      s: function (c) {
        return "segon" + (c === 1 ? "" : "s");
      },
      ms: function (c) {
        return "milisegon" + (c === 1 ? "" : "s");
      },
      decimal: ","
    },
    cs: {
      y: function (c) {
        return ["rok", "roku", "roky", "let"][getCzechOrSlovakForm(c)];
      },
      mo: function (c) {
        return ["měsíc", "měsíce", "měsíce", "měsíců"][getCzechOrSlovakForm(c)];
      },
      w: function (c) {
        return ["týden", "týdne", "týdny", "týdnů"][getCzechOrSlovakForm(c)];
      },
      d: function (c) {
        return ["den", "dne", "dny", "dní"][getCzechOrSlovakForm(c)];
      },
      h: function (c) {
        return ["hodina", "hodiny", "hodiny", "hodin"][getCzechOrSlovakForm(c)];
      },
      m: function (c) {
        return ["minuta", "minuty", "minuty", "minut"][getCzechOrSlovakForm(c)];
      },
      s: function (c) {
        return ["sekunda", "sekundy", "sekundy", "sekund"][
          getCzechOrSlovakForm(c)
        ];
      },
      ms: function (c) {
        return ["milisekunda", "milisekundy", "milisekundy", "milisekund"][
          getCzechOrSlovakForm(c)
        ];
      },
      decimal: ","
    },
    cy: {
      y: "flwyddyn",
      mo: "mis",
      w: "wythnos",
      d: "diwrnod",
      h: "awr",
      m: "munud",
      s: "eiliad",
      ms: "milieiliad"
    },
    da: {
      y: "år",
      mo: function (c) {
        return "måned" + (c === 1 ? "" : "er");
      },
      w: function (c) {
        return "uge" + (c === 1 ? "" : "r");
      },
      d: function (c) {
        return "dag" + (c === 1 ? "" : "e");
      },
      h: function (c) {
        return "time" + (c === 1 ? "" : "r");
      },
      m: function (c) {
        return "minut" + (c === 1 ? "" : "ter");
      },
      s: function (c) {
        return "sekund" + (c === 1 ? "" : "er");
      },
      ms: function (c) {
        return "millisekund" + (c === 1 ? "" : "er");
      },
      decimal: ","
    },
    de: {
      y: function (c) {
        return "Jahr" + (c === 1 ? "" : "e");
      },
      mo: function (c) {
        return "Monat" + (c === 1 ? "" : "e");
      },
      w: function (c) {
        return "Woche" + (c === 1 ? "" : "n");
      },
      d: function (c) {
        return "Tag" + (c === 1 ? "" : "e");
      },
      h: function (c) {
        return "Stunde" + (c === 1 ? "" : "n");
      },
      m: function (c) {
        return "Minute" + (c === 1 ? "" : "n");
      },
      s: function (c) {
        return "Sekunde" + (c === 1 ? "" : "n");
      },
      ms: function (c) {
        return "Millisekunde" + (c === 1 ? "" : "n");
      },
      decimal: ","
    },
    el: greek,
    en: {
      y: function (c) {
        return "year" + (c === 1 ? "" : "s");
      },
      mo: function (c) {
        return "month" + (c === 1 ? "" : "s");
      },
      w: function (c) {
        return "week" + (c === 1 ? "" : "s");
      },
      d: function (c) {
        return "day" + (c === 1 ? "" : "s");
      },
      h: function (c) {
        return "hour" + (c === 1 ? "" : "s");
      },
      m: function (c) {
        return "minute" + (c === 1 ? "" : "s");
      },
      s: function (c) {
        return "second" + (c === 1 ? "" : "s");
      },
      ms: function (c) {
        return "millisecond" + (c === 1 ? "" : "s");
      },
      decimal: "."
    },
    eo: {
      y: function (c) {
        return "jaro" + (c === 1 ? "" : "j");
      },
      mo: function (c) {
        return "monato" + (c === 1 ? "" : "j");
      },
      w: function (c) {
        return "semajno" + (c === 1 ? "" : "j");
      },
      d: function (c) {
        return "tago" + (c === 1 ? "" : "j");
      },
      h: function (c) {
        return "horo" + (c === 1 ? "" : "j");
      },
      m: function (c) {
        return "minuto" + (c === 1 ? "" : "j");
      },
      s: function (c) {
        return "sekundo" + (c === 1 ? "" : "j");
      },
      ms: function (c) {
        return "milisekundo" + (c === 1 ? "" : "j");
      },
      decimal: ","
    },
    es: {
      y: function (c) {
        return "año" + (c === 1 ? "" : "s");
      },
      mo: function (c) {
        return "mes" + (c === 1 ? "" : "es");
      },
      w: function (c) {
        return "semana" + (c === 1 ? "" : "s");
      },
      d: function (c) {
        return "día" + (c === 1 ? "" : "s");
      },
      h: function (c) {
        return "hora" + (c === 1 ? "" : "s");
      },
      m: function (c) {
        return "minuto" + (c === 1 ? "" : "s");
      },
      s: function (c) {
        return "segundo" + (c === 1 ? "" : "s");
      },
      ms: function (c) {
        return "milisegundo" + (c === 1 ? "" : "s");
      },
      decimal: ","
    },
    et: {
      y: function (c) {
        return "aasta" + (c === 1 ? "" : "t");
      },
      mo: function (c) {
        return "kuu" + (c === 1 ? "" : "d");
      },
      w: function (c) {
        return "nädal" + (c === 1 ? "" : "at");
      },
      d: function (c) {
        return "päev" + (c === 1 ? "" : "a");
      },
      h: function (c) {
        return "tund" + (c === 1 ? "" : "i");
      },
      m: function (c) {
        return "minut" + (c === 1 ? "" : "it");
      },
      s: function (c) {
        return "sekund" + (c === 1 ? "" : "it");
      },
      ms: function (c) {
        return "millisekund" + (c === 1 ? "" : "it");
      },
      decimal: ","
    },
    eu: {
      y: "urte",
      mo: "hilabete",
      w: "aste",
      d: "egun",
      h: "ordu",
      m: "minutu",
      s: "segundo",
      ms: "milisegundo",
      decimal: ","
    },
    fa: {
      y: "سال",
      mo: "ماه",
      w: "هفته",
      d: "روز",
      h: "ساعت",
      m: "دقیقه",
      s: "ثانیه",
      ms: "میلی ثانیه",
      decimal: "."
    },
    fi: {
      y: function (c) {
        return c === 1 ? "vuosi" : "vuotta";
      },
      mo: function (c) {
        return c === 1 ? "kuukausi" : "kuukautta";
      },
      w: function (c) {
        return "viikko" + (c === 1 ? "" : "a");
      },
      d: function (c) {
        return "päivä" + (c === 1 ? "" : "ä");
      },
      h: function (c) {
        return "tunti" + (c === 1 ? "" : "a");
      },
      m: function (c) {
        return "minuutti" + (c === 1 ? "" : "a");
      },
      s: function (c) {
        return "sekunti" + (c === 1 ? "" : "a");
      },
      ms: function (c) {
        return "millisekunti" + (c === 1 ? "" : "a");
      },
      decimal: ","
    },
    fo: {
      y: "ár",
      mo: function (c) {
        return c === 1 ? "mánaður" : "mánaðir";
      },
      w: function (c) {
        return c === 1 ? "vika" : "vikur";
      },
      d: function (c) {
        return c === 1 ? "dagur" : "dagar";
      },
      h: function (c) {
        return c === 1 ? "tími" : "tímar";
      },
      m: function (c) {
        return c === 1 ? "minuttur" : "minuttir";
      },
      s: "sekund",
      ms: "millisekund",
      decimal: ","
    },
    fr: {
      y: function (c) {
        return "an" + (c >= 2 ? "s" : "");
      },
      mo: "mois",
      w: function (c) {
        return "semaine" + (c >= 2 ? "s" : "");
      },
      d: function (c) {
        return "jour" + (c >= 2 ? "s" : "");
      },
      h: function (c) {
        return "heure" + (c >= 2 ? "s" : "");
      },
      m: function (c) {
        return "minute" + (c >= 2 ? "s" : "");
      },
      s: function (c) {
        return "seconde" + (c >= 2 ? "s" : "");
      },
      ms: function (c) {
        return "milliseconde" + (c >= 2 ? "s" : "");
      },
      decimal: ","
    },
    gr: greek,
    he: {
      y: function (c) {
        return c === 1 ? "שנה" : "שנים";
      },
      mo: function (c) {
        return c === 1 ? "חודש" : "חודשים";
      },
      w: function (c) {
        return c === 1 ? "שבוע" : "שבועות";
      },
      d: function (c) {
        return c === 1 ? "יום" : "ימים";
      },
      h: function (c) {
        return c === 1 ? "שעה" : "שעות";
      },
      m: function (c) {
        return c === 1 ? "דקה" : "דקות";
      },
      s: function (c) {
        return c === 1 ? "שניה" : "שניות";
      },
      ms: function (c) {
        return c === 1 ? "מילישנייה" : "מילישניות";
      },
      decimal: "."
    },
    hr: {
      y: function (c) {
        if (c % 10 === 2 || c % 10 === 3 || c % 10 === 4) {
          return "godine";
        }
        return "godina";
      },
      mo: function (c) {
        if (c === 1) {
          return "mjesec";
        } else if (c === 2 || c === 3 || c === 4) {
          return "mjeseca";
        }
        return "mjeseci";
      },
      w: function (c) {
        if (c % 10 === 1 && c !== 11) {
          return "tjedan";
        }
        return "tjedna";
      },
      d: function (c) {
        return c === 1 ? "dan" : "dana";
      },
      h: function (c) {
        if (c === 1) {
          return "sat";
        } else if (c === 2 || c === 3 || c === 4) {
          return "sata";
        }
        return "sati";
      },
      m: function (c) {
        var mod10 = c % 10;
        if ((mod10 === 2 || mod10 === 3 || mod10 === 4) && (c < 10 || c > 14)) {
          return "minute";
        }
        return "minuta";
      },
      s: function (c) {
        var mod10 = c % 10;
        if (mod10 === 5 || (Math.floor(c) === c && c >= 10 && c <= 19)) {
          return "sekundi";
        } else if (mod10 === 1) {
          return "sekunda";
        } else if (mod10 === 2 || mod10 === 3 || mod10 === 4) {
          return "sekunde";
        }
        return "sekundi";
      },
      ms: function (c) {
        if (c === 1) {
          return "milisekunda";
        } else if (c % 10 === 2 || c % 10 === 3 || c % 10 === 4) {
          return "milisekunde";
        }
        return "milisekundi";
      },
      decimal: ","
    },
    hi: {
      y: "साल",
      mo: function (c) {
        return c === 1 ? "महीना" : "महीने";
      },
      w: function (c) {
        return c === 1 ? "हफ़्ता" : "हफ्ते";
      },
      d: "दिन",
      h: function (c) {
        return c === 1 ? "घंटा" : "घंटे";
      },
      m: "मिनट",
      s: "सेकंड",
      ms: "मिलीसेकंड",
      decimal: "."
    },
    hu: {
      y: "év",
      mo: "hónap",
      w: "hét",
      d: "nap",
      h: "óra",
      m: "perc",
      s: "másodperc",
      ms: "ezredmásodperc",
      decimal: ","
    },
    id: {
      y: "tahun",
      mo: "bulan",
      w: "minggu",
      d: "hari",
      h: "jam",
      m: "menit",
      s: "detik",
      ms: "milidetik",
      decimal: "."
    },
    is: {
      y: "ár",
      mo: function (c) {
        return "mánuð" + (c === 1 ? "ur" : "ir");
      },
      w: function (c) {
        return "vik" + (c === 1 ? "a" : "ur");
      },
      d: function (c) {
        return "dag" + (c === 1 ? "ur" : "ar");
      },
      h: function (c) {
        return "klukkutím" + (c === 1 ? "i" : "ar");
      },
      m: function (c) {
        return "mínút" + (c === 1 ? "a" : "ur");
      },
      s: function (c) {
        return "sekúnd" + (c === 1 ? "a" : "ur");
      },
      ms: function (c) {
        return "millisekúnd" + (c === 1 ? "a" : "ur");
      },
      decimal: "."
    },
    it: {
      y: function (c) {
        return "ann" + (c === 1 ? "o" : "i");
      },
      mo: function (c) {
        return "mes" + (c === 1 ? "e" : "i");
      },
      w: function (c) {
        return "settiman" + (c === 1 ? "a" : "e");
      },
      d: function (c) {
        return "giorn" + (c === 1 ? "o" : "i");
      },
      h: function (c) {
        return "or" + (c === 1 ? "a" : "e");
      },
      m: function (c) {
        return "minut" + (c === 1 ? "o" : "i");
      },
      s: function (c) {
        return "second" + (c === 1 ? "o" : "i");
      },
      ms: function (c) {
        return "millisecond" + (c === 1 ? "o" : "i");
      },
      decimal: ","
    },
    ja: {
      y: "年",
      mo: "ヶ月",
      w: "週",
      d: "日",
      h: "時間",
      m: "分",
      s: "秒",
      ms: "ミリ秒",
      decimal: "."
    },
    km: {
      y: "ឆ្នាំ",
      mo: "ខែ",
      w: "សប្តាហ៍",
      d: "ថ្ងៃ",
      h: "ម៉ោង",
      m: "នាទី",
      s: "វិនាទី",
      ms: "មិល្លីវិនាទី"
    },
    kn: {
      y: function (c) {
        return c === 1 ? "ವರ್ಷ" : "ವರ್ಷಗಳು";
      },
      mo: function (c) {
        return c === 1 ? "ತಿಂಗಳು" : "ತಿಂಗಳುಗಳು";
      },
      w: function (c) {
        return c === 1 ? "ವಾರ" : "ವಾರಗಳು";
      },
      d: function (c) {
        return c === 1 ? "ದಿನ" : "ದಿನಗಳು";
      },
      h: function (c) {
        return c === 1 ? "ಗಂಟೆ" : "ಗಂಟೆಗಳು";
      },
      m: function (c) {
        return c === 1 ? "ನಿಮಿಷ" : "ನಿಮಿಷಗಳು";
      },
      s: function (c) {
        return c === 1 ? "ಸೆಕೆಂಡ್" : "ಸೆಕೆಂಡುಗಳು";
      },
      ms: function (c) {
        return c === 1 ? "ಮಿಲಿಸೆಕೆಂಡ್" : "ಮಿಲಿಸೆಕೆಂಡುಗಳು";
      }
    },
    ko: {
      y: "년",
      mo: "개월",
      w: "주일",
      d: "일",
      h: "시간",
      m: "분",
      s: "초",
      ms: "밀리 초",
      decimal: "."
    },
    ku: {
      y: "sal",
      mo: "meh",
      w: "hefte",
      d: "roj",
      h: "seet",
      m: "deqe",
      s: "saniye",
      ms: "mîlîçirk",
      decimal: ","
    },
    lo: {
      y: "ປີ",
      mo: "ເດືອນ",
      w: "ອາທິດ",
      d: "ມື້",
      h: "ຊົ່ວໂມງ",
      m: "ນາທີ",
      s: "ວິນາທີ",
      ms: "ມິນລິວິນາທີ",
      decimal: ","
    },
    lt: {
      y: function (c) {
        return c % 10 === 0 || (c % 100 >= 10 && c % 100 <= 20)
          ? "metų"
          : "metai";
      },
      mo: function (c) {
        return ["mėnuo", "mėnesiai", "mėnesių"][getLithuanianForm(c)];
      },
      w: function (c) {
        return ["savaitė", "savaitės", "savaičių"][getLithuanianForm(c)];
      },
      d: function (c) {
        return ["diena", "dienos", "dienų"][getLithuanianForm(c)];
      },
      h: function (c) {
        return ["valanda", "valandos", "valandų"][getLithuanianForm(c)];
      },
      m: function (c) {
        return ["minutė", "minutės", "minučių"][getLithuanianForm(c)];
      },
      s: function (c) {
        return ["sekundė", "sekundės", "sekundžių"][getLithuanianForm(c)];
      },
      ms: function (c) {
        return ["milisekundė", "milisekundės", "milisekundžių"][
          getLithuanianForm(c)
        ];
      },
      decimal: ","
    },
    lv: {
      y: function (c) {
        return getLatvianForm(c) ? "gads" : "gadi";
      },
      mo: function (c) {
        return getLatvianForm(c) ? "mēnesis" : "mēneši";
      },
      w: function (c) {
        return getLatvianForm(c) ? "nedēļa" : "nedēļas";
      },
      d: function (c) {
        return getLatvianForm(c) ? "diena" : "dienas";
      },
      h: function (c) {
        return getLatvianForm(c) ? "stunda" : "stundas";
      },
      m: function (c) {
        return getLatvianForm(c) ? "minūte" : "minūtes";
      },
      s: function (c) {
        return getLatvianForm(c) ? "sekunde" : "sekundes";
      },
      ms: function (c) {
        return getLatvianForm(c) ? "milisekunde" : "milisekundes";
      },
      decimal: ","
    },
    mk: {
      y: function (c) {
        return c === 1 ? "година" : "години";
      },
      mo: function (c) {
        return c === 1 ? "месец" : "месеци";
      },
      w: function (c) {
        return c === 1 ? "недела" : "недели";
      },
      d: function (c) {
        return c === 1 ? "ден" : "дена";
      },
      h: function (c) {
        return c === 1 ? "час" : "часа";
      },
      m: function (c) {
        return c === 1 ? "минута" : "минути";
      },
      s: function (c) {
        return c === 1 ? "секунда" : "секунди";
      },
      ms: function (c) {
        return c === 1 ? "милисекунда" : "милисекунди";
      },
      decimal: ","
    },
    mn: {
      y: "жил",
      mo: "сар",
      w: "долоо хоног",
      d: "өдөр",
      h: "цаг",
      m: "минут",
      s: "секунд",
      ms: "миллисекунд",
      decimal: "."
    },
    mr: {
      y: function (c) {
        return c === 1 ? "वर्ष" : "वर्षे";
      },
      mo: function (c) {
        return c === 1 ? "महिना" : "महिने";
      },
      w: function (c) {
        return c === 1 ? "आठवडा" : "आठवडे";
      },
      d: "दिवस",
      h: "तास",
      m: function (c) {
        return c === 1 ? "मिनिट" : "मिनिटे";
      },
      s: "सेकंद",
      ms: "मिलिसेकंद"
    },
    ms: {
      y: "tahun",
      mo: "bulan",
      w: "minggu",
      d: "hari",
      h: "jam",
      m: "minit",
      s: "saat",
      ms: "milisaat",
      decimal: "."
    },
    nl: {
      y: "jaar",
      mo: function (c) {
        return c === 1 ? "maand" : "maanden";
      },
      w: function (c) {
        return c === 1 ? "week" : "weken";
      },
      d: function (c) {
        return c === 1 ? "dag" : "dagen";
      },
      h: "uur",
      m: function (c) {
        return c === 1 ? "minuut" : "minuten";
      },
      s: function (c) {
        return c === 1 ? "seconde" : "seconden";
      },
      ms: function (c) {
        return c === 1 ? "milliseconde" : "milliseconden";
      },
      decimal: ","
    },
    no: {
      y: "år",
      mo: function (c) {
        return "måned" + (c === 1 ? "" : "er");
      },
      w: function (c) {
        return "uke" + (c === 1 ? "" : "r");
      },
      d: function (c) {
        return "dag" + (c === 1 ? "" : "er");
      },
      h: function (c) {
        return "time" + (c === 1 ? "" : "r");
      },
      m: function (c) {
        return "minutt" + (c === 1 ? "" : "er");
      },
      s: function (c) {
        return "sekund" + (c === 1 ? "" : "er");
      },
      ms: function (c) {
        return "millisekund" + (c === 1 ? "" : "er");
      },
      decimal: ","
    },
    pl: {
      y: function (c) {
        return ["rok", "roku", "lata", "lat"][getPolishForm(c)];
      },
      mo: function (c) {
        return ["miesiąc", "miesiąca", "miesiące", "miesięcy"][
          getPolishForm(c)
        ];
      },
      w: function (c) {
        return ["tydzień", "tygodnia", "tygodnie", "tygodni"][getPolishForm(c)];
      },
      d: function (c) {
        return ["dzień", "dnia", "dni", "dni"][getPolishForm(c)];
      },
      h: function (c) {
        return ["godzina", "godziny", "godziny", "godzin"][getPolishForm(c)];
      },
      m: function (c) {
        return ["minuta", "minuty", "minuty", "minut"][getPolishForm(c)];
      },
      s: function (c) {
        return ["sekunda", "sekundy", "sekundy", "sekund"][getPolishForm(c)];
      },
      ms: function (c) {
        return ["milisekunda", "milisekundy", "milisekundy", "milisekund"][
          getPolishForm(c)
        ];
      },
      decimal: ","
    },
    pt: {
      y: function (c) {
        return "ano" + (c === 1 ? "" : "s");
      },
      mo: function (c) {
        return c === 1 ? "mês" : "meses";
      },
      w: function (c) {
        return "semana" + (c === 1 ? "" : "s");
      },
      d: function (c) {
        return "dia" + (c === 1 ? "" : "s");
      },
      h: function (c) {
        return "hora" + (c === 1 ? "" : "s");
      },
      m: function (c) {
        return "minuto" + (c === 1 ? "" : "s");
      },
      s: function (c) {
        return "segundo" + (c === 1 ? "" : "s");
      },
      ms: function (c) {
        return "milissegundo" + (c === 1 ? "" : "s");
      },
      decimal: ","
    },
    ro: {
      y: function (c) {
        return c === 1 ? "an" : "ani";
      },
      mo: function (c) {
        return c === 1 ? "lună" : "luni";
      },
      w: function (c) {
        return c === 1 ? "săptămână" : "săptămâni";
      },
      d: function (c) {
        return c === 1 ? "zi" : "zile";
      },
      h: function (c) {
        return c === 1 ? "oră" : "ore";
      },
      m: function (c) {
        return c === 1 ? "minut" : "minute";
      },
      s: function (c) {
        return c === 1 ? "secundă" : "secunde";
      },
      ms: function (c) {
        return c === 1 ? "milisecundă" : "milisecunde";
      },
      decimal: ","
    },
    ru: {
      y: function (c) {
        return ["лет", "год", "года"][getSlavicForm(c)];
      },
      mo: function (c) {
        return ["месяцев", "месяц", "месяца"][getSlavicForm(c)];
      },
      w: function (c) {
        return ["недель", "неделя", "недели"][getSlavicForm(c)];
      },
      d: function (c) {
        return ["дней", "день", "дня"][getSlavicForm(c)];
      },
      h: function (c) {
        return ["часов", "час", "часа"][getSlavicForm(c)];
      },
      m: function (c) {
        return ["минут", "минута", "минуты"][getSlavicForm(c)];
      },
      s: function (c) {
        return ["секунд", "секунда", "секунды"][getSlavicForm(c)];
      },
      ms: function (c) {
        return ["миллисекунд", "миллисекунда", "миллисекунды"][
          getSlavicForm(c)
        ];
      },
      decimal: ","
    },
    sq: {
      y: function (c) {
        return c === 1 ? "vit" : "vjet";
      },
      mo: "muaj",
      w: "javë",
      d: "ditë",
      h: "orë",
      m: function (c) {
        return "minut" + (c === 1 ? "ë" : "a");
      },
      s: function (c) {
        return "sekond" + (c === 1 ? "ë" : "a");
      },
      ms: function (c) {
        return "milisekond" + (c === 1 ? "ë" : "a");
      },
      decimal: ","
    },
    sr: {
      y: function (c) {
        return ["години", "година", "године"][getSlavicForm(c)];
      },
      mo: function (c) {
        return ["месеци", "месец", "месеца"][getSlavicForm(c)];
      },
      w: function (c) {
        return ["недељи", "недеља", "недеље"][getSlavicForm(c)];
      },
      d: function (c) {
        return ["дани", "дан", "дана"][getSlavicForm(c)];
      },
      h: function (c) {
        return ["сати", "сат", "сата"][getSlavicForm(c)];
      },
      m: function (c) {
        return ["минута", "минут", "минута"][getSlavicForm(c)];
      },
      s: function (c) {
        return ["секунди", "секунда", "секунде"][getSlavicForm(c)];
      },
      ms: function (c) {
        return ["милисекунди", "милисекунда", "милисекунде"][getSlavicForm(c)];
      },
      decimal: ","
    },
    ta: {
      y: function (c) {
        return c === 1 ? "வருடம்" : "ஆண்டுகள்";
      },
      mo: function (c) {
        return c === 1 ? "மாதம்" : "மாதங்கள்";
      },
      w: function (c) {
        return c === 1 ? "வாரம்" : "வாரங்கள்";
      },
      d: function (c) {
        return c === 1 ? "நாள்" : "நாட்கள்";
      },
      h: function (c) {
        return c === 1 ? "மணி" : "மணிநேரம்";
      },
      m: function (c) {
        return "நிமிட" + (c === 1 ? "ம்" : "ங்கள்");
      },
      s: function (c) {
        return "வினாடி" + (c === 1 ? "" : "கள்");
      },
      ms: function (c) {
        return "மில்லி விநாடி" + (c === 1 ? "" : "கள்");
      }
    },
    te: {
      y: function (c) {
        return "సంవత్స" + (c === 1 ? "రం" : "రాల");
      },
      mo: function (c) {
        return "నెల" + (c === 1 ? "" : "ల");
      },
      w: function (c) {
        return c === 1 ? "వారం" : "వారాలు";
      },
      d: function (c) {
        return "రోజు" + (c === 1 ? "" : "లు");
      },
      h: function (c) {
        return "గంట" + (c === 1 ? "" : "లు");
      },
      m: function (c) {
        return c === 1 ? "నిమిషం" : "నిమిషాలు";
      },
      s: function (c) {
        return c === 1 ? "సెకను" : "సెకన్లు";
      },
      ms: function (c) {
        return c === 1 ? "మిల్లీసెకన్" : "మిల్లీసెకన్లు";
      }
    },
    uk: {
      y: function (c) {
        return ["років", "рік", "роки"][getSlavicForm(c)];
      },
      mo: function (c) {
        return ["місяців", "місяць", "місяці"][getSlavicForm(c)];
      },
      w: function (c) {
        return ["тижнів", "тиждень", "тижні"][getSlavicForm(c)];
      },
      d: function (c) {
        return ["днів", "день", "дні"][getSlavicForm(c)];
      },
      h: function (c) {
        return ["годин", "година", "години"][getSlavicForm(c)];
      },
      m: function (c) {
        return ["хвилин", "хвилина", "хвилини"][getSlavicForm(c)];
      },
      s: function (c) {
        return ["секунд", "секунда", "секунди"][getSlavicForm(c)];
      },
      ms: function (c) {
        return ["мілісекунд", "мілісекунда", "мілісекунди"][getSlavicForm(c)];
      },
      decimal: ","
    },
    ur: {
      y: "سال",
      mo: function (c) {
        return c === 1 ? "مہینہ" : "مہینے";
      },
      w: function (c) {
        return c === 1 ? "ہفتہ" : "ہفتے";
      },
      d: "دن",
      h: function (c) {
        return c === 1 ? "گھنٹہ" : "گھنٹے";
      },
      m: "منٹ",
      s: "سیکنڈ",
      ms: "ملی سیکنڈ",
      decimal: "."
    },
    sk: {
      y: function (c) {
        return ["rok", "roky", "roky", "rokov"][getCzechOrSlovakForm(c)];
      },
      mo: function (c) {
        return ["mesiac", "mesiace", "mesiace", "mesiacov"][
          getCzechOrSlovakForm(c)
        ];
      },
      w: function (c) {
        return ["týždeň", "týždne", "týždne", "týždňov"][
          getCzechOrSlovakForm(c)
        ];
      },
      d: function (c) {
        return ["deň", "dni", "dni", "dní"][getCzechOrSlovakForm(c)];
      },
      h: function (c) {
        return ["hodina", "hodiny", "hodiny", "hodín"][getCzechOrSlovakForm(c)];
      },
      m: function (c) {
        return ["minúta", "minúty", "minúty", "minút"][getCzechOrSlovakForm(c)];
      },
      s: function (c) {
        return ["sekunda", "sekundy", "sekundy", "sekúnd"][
          getCzechOrSlovakForm(c)
        ];
      },
      ms: function (c) {
        return ["milisekunda", "milisekundy", "milisekundy", "milisekúnd"][
          getCzechOrSlovakForm(c)
        ];
      },
      decimal: ","
    },
    sl: {
      y: function (c) {
        if (c % 10 === 1) {
          return "leto";
        } else if (c % 100 === 2) {
          return "leti";
        } else if (
          c % 100 === 3 ||
          c % 100 === 4 ||
          (Math.floor(c) !== c && c % 100 <= 5)
        ) {
          return "leta";
        } else {
          return "let";
        }
      },
      mo: function (c) {
        if (c % 10 === 1) {
          return "mesec";
        } else if (c % 100 === 2 || (Math.floor(c) !== c && c % 100 <= 5)) {
          return "meseca";
        } else if (c % 10 === 3 || c % 10 === 4) {
          return "mesece";
        } else {
          return "mesecev";
        }
      },
      w: function (c) {
        if (c % 10 === 1) {
          return "teden";
        } else if (c % 10 === 2 || (Math.floor(c) !== c && c % 100 <= 4)) {
          return "tedna";
        } else if (c % 10 === 3 || c % 10 === 4) {
          return "tedne";
        } else {
          return "tednov";
        }
      },
      d: function (c) {
        return c % 100 === 1 ? "dan" : "dni";
      },
      h: function (c) {
        if (c % 10 === 1) {
          return "ura";
        } else if (c % 100 === 2) {
          return "uri";
        } else if (c % 10 === 3 || c % 10 === 4 || Math.floor(c) !== c) {
          return "ure";
        } else {
          return "ur";
        }
      },
      m: function (c) {
        if (c % 10 === 1) {
          return "minuta";
        } else if (c % 10 === 2) {
          return "minuti";
        } else if (
          c % 10 === 3 ||
          c % 10 === 4 ||
          (Math.floor(c) !== c && c % 100 <= 4)
        ) {
          return "minute";
        } else {
          return "minut";
        }
      },
      s: function (c) {
        if (c % 10 === 1) {
          return "sekunda";
        } else if (c % 100 === 2) {
          return "sekundi";
        } else if (c % 100 === 3 || c % 100 === 4 || Math.floor(c) !== c) {
          return "sekunde";
        } else {
          return "sekund";
        }
      },
      ms: function (c) {
        if (c % 10 === 1) {
          return "milisekunda";
        } else if (c % 100 === 2) {
          return "milisekundi";
        } else if (c % 100 === 3 || c % 100 === 4 || Math.floor(c) !== c) {
          return "milisekunde";
        } else {
          return "milisekund";
        }
      },
      decimal: ","
    },
    sv: {
      y: "år",
      mo: function (c) {
        return "månad" + (c === 1 ? "" : "er");
      },
      w: function (c) {
        return "veck" + (c === 1 ? "a" : "or");
      },
      d: function (c) {
        return "dag" + (c === 1 ? "" : "ar");
      },
      h: function (c) {
        return "timm" + (c === 1 ? "e" : "ar");
      },
      m: function (c) {
        return "minut" + (c === 1 ? "" : "er");
      },
      s: function (c) {
        return "sekund" + (c === 1 ? "" : "er");
      },
      ms: function (c) {
        return "millisekund" + (c === 1 ? "" : "er");
      },
      decimal: ","
    },
    sw: {
      y: function (c) {
        return c === 1 ? "mwaka" : "miaka";
      },
      mo: function (c) {
        return c === 1 ? "mwezi" : "miezi";
      },
      w: "wiki",
      d: function (c) {
        return c === 1 ? "siku" : "masiku";
      },
      h: function (c) {
        return c === 1 ? "saa" : "masaa";
      },
      m: "dakika",
      s: "sekunde",
      ms: "milisekunde",
      decimal: ".",
      _numberFirst: true
    },
    tr: {
      y: "yıl",
      mo: "ay",
      w: "hafta",
      d: "gün",
      h: "saat",
      m: "dakika",
      s: "saniye",
      ms: "milisaniye",
      decimal: ","
    },
    th: {
      y: "ปี",
      mo: "เดือน",
      w: "สัปดาห์",
      d: "วัน",
      h: "ชั่วโมง",
      m: "นาที",
      s: "วินาที",
      ms: "มิลลิวินาที",
      decimal: "."
    },
    vi: {
      y: "năm",
      mo: "tháng",
      w: "tuần",
      d: "ngày",
      h: "giờ",
      m: "phút",
      s: "giây",
      ms: "mili giây",
      decimal: ","
    },
    zh_CN: {
      y: "年",
      mo: "个月",
      w: "周",
      d: "天",
      h: "小时",
      m: "分钟",
      s: "秒",
      ms: "毫秒",
      decimal: "."
    },
    zh_TW: {
      y: "年",
      mo: "個月",
      w: "周",
      d: "天",
      h: "小時",
      m: "分鐘",
      s: "秒",
      ms: "毫秒",
      decimal: "."
    }
  };

  // You can create a humanizer, which returns a function with default
  // parameters.
  function humanizer(passedOptions) {
    var result = function humanizer(ms, humanizerOptions) {
      var options = assign({}, result, humanizerOptions || {});
      return doHumanization(ms, options);
    };

    return assign(
      result,
      {
        language: "en",
        spacer: " ",
        conjunction: "",
        serialComma: true,
        units: ["y", "mo", "w", "d", "h", "m", "s"],
        languages: {},
        round: false,
        unitMeasures: {
          y: 31557600000,
          mo: 2629800000,
          w: 604800000,
          d: 86400000,
          h: 3600000,
          m: 60000,
          s: 1000,
          ms: 1
        }
      },
      passedOptions
    );
  }

  // The main function is just a wrapper around a default humanizer.
  var humanizeDuration = humanizer({});

  // Build dictionary from options
  function getDictionary(options) {
    var languagesFromOptions = [options.language];

    if (has(options, "fallbacks")) {
      if (isArray(options.fallbacks) && options.fallbacks.length) {
        languagesFromOptions = languagesFromOptions.concat(options.fallbacks);
      } else {
        throw new Error("fallbacks must be an array with at least one element");
      }
    }

    for (var i = 0; i < languagesFromOptions.length; i++) {
      var languageToTry = languagesFromOptions[i];
      if (has(options.languages, languageToTry)) {
        return options.languages[languageToTry];
      } else if (has(LANGUAGES, languageToTry)) {
        return LANGUAGES[languageToTry];
      }
    }

    throw new Error("No language found.");
  }

  // doHumanization does the bulk of the work.
  function doHumanization(ms, options) {
    var i, len, piece;

    // Make sure we have a positive number.
    // Has the nice sideffect of turning Number objects into primitives.
    ms = Math.abs(ms);

    var dictionary = getDictionary(options);
    var pieces = [];

    // Start at the top and keep removing units, bit by bit.
    var unitName, unitMS, unitCount;
    for (i = 0, len = options.units.length; i < len; i++) {
      unitName = options.units[i];
      unitMS = options.unitMeasures[unitName];

      // What's the number of full units we can fit?
      if (i + 1 === len) {
        if (has(options, "maxDecimalPoints")) {
          // We need to use this expValue to avoid rounding functionality of toFixed call
          var expValue = Math.pow(10, options.maxDecimalPoints);
          var unitCountFloat = ms / unitMS;
          unitCount = parseFloat(
            (Math.floor(expValue * unitCountFloat) / expValue).toFixed(
              options.maxDecimalPoints
            )
          );
        } else {
          unitCount = ms / unitMS;
        }
      } else {
        unitCount = Math.floor(ms / unitMS);
      }

      // Add the string.
      pieces.push({
        unitCount: unitCount,
        unitName: unitName
      });

      // Remove what we just figured out.
      ms -= unitCount * unitMS;
    }

    var firstOccupiedUnitIndex = 0;
    for (i = 0; i < pieces.length; i++) {
      if (pieces[i].unitCount) {
        firstOccupiedUnitIndex = i;
        break;
      }
    }

    if (options.round) {
      var ratioToLargerUnit, previousPiece;
      for (i = pieces.length - 1; i >= 0; i--) {
        piece = pieces[i];
        piece.unitCount = Math.round(piece.unitCount);

        if (i === 0) {
          break;
        }

        previousPiece = pieces[i - 1];

        ratioToLargerUnit =
          options.unitMeasures[previousPiece.unitName] /
          options.unitMeasures[piece.unitName];
        if (
          piece.unitCount % ratioToLargerUnit === 0 ||
          (options.largest && options.largest - 1 < i - firstOccupiedUnitIndex)
        ) {
          previousPiece.unitCount += piece.unitCount / ratioToLargerUnit;
          piece.unitCount = 0;
        }
      }
    }

    var result = [];
    for (i = 0, pieces.length; i < len; i++) {
      piece = pieces[i];
      if (piece.unitCount) {
        result.push(
          render(piece.unitCount, piece.unitName, dictionary, options)
        );
      }

      if (result.length === options.largest) {
        break;
      }
    }

    if (result.length) {
      var delimiter;
      if (has(options, "delimiter")) {
        delimiter = options.delimiter;
      } else if (has(dictionary, "delimiter")) {
        delimiter = dictionary.delimiter;
      } else {
        delimiter = ", ";
      }

      if (!options.conjunction || result.length === 1) {
        return result.join(delimiter);
      } else if (result.length === 2) {
        return result.join(options.conjunction);
      } else if (result.length > 2) {
        return (
          result.slice(0, -1).join(delimiter) +
          (options.serialComma ? "," : "") +
          options.conjunction +
          result.slice(-1)
        );
      }
    } else {
      return render(
        0,
        options.units[options.units.length - 1],
        dictionary,
        options
      );
    }
  }

  function render(count, type, dictionary, options) {
    var decimal;
    if (has(options, "decimal")) {
      decimal = options.decimal;
    } else if (has(dictionary, "decimal")) {
      decimal = dictionary.decimal;
    } else {
      decimal = ".";
    }

    var countStr;
    if (typeof dictionary._formatCount === "function") {
      countStr = dictionary._formatCount(count, decimal);
    } else {
      countStr = count.toString().replace(".", decimal);
    }

    var dictionaryValue = dictionary[type];
    var word;
    if (typeof dictionaryValue === "function") {
      word = dictionaryValue(count);
    } else {
      word = dictionaryValue;
    }

    if (dictionary._numberFirst) {
      return word + options.spacer + countStr;
    }
    return countStr + options.spacer + word;
  }

  function assign(destination) {
    var source;
    for (var i = 1; i < arguments.length; i++) {
      source = arguments[i];
      for (var prop in source) {
        if (has(source, prop)) {
          destination[prop] = source[prop];
        }
      }
    }
    return destination;
  }

  function getArabicForm(c) {
    if (c === 1) {
      return 0;
    }
    if (c === 2) {
      return 1;
    }
    if (c > 2 && c < 11) {
      return 2;
    }
    return 0;
  }

  function getPolishForm(c) {
    if (c === 1) {
      return 0;
    } else if (Math.floor(c) !== c) {
      return 1;
    } else if (c % 10 >= 2 && c % 10 <= 4 && !(c % 100 > 10 && c % 100 < 20)) {
      return 2;
    } else {
      return 3;
    }
  }

  function getSlavicForm(c) {
    if (Math.floor(c) !== c) {
      return 2;
    } else if (
      (c % 100 >= 5 && c % 100 <= 20) ||
      (c % 10 >= 5 && c % 10 <= 9) ||
      c % 10 === 0
    ) {
      return 0;
    } else if (c % 10 === 1) {
      return 1;
    } else if (c > 1) {
      return 2;
    } else {
      return 0;
    }
  }

  function getCzechOrSlovakForm(c) {
    if (c === 1) {
      return 0;
    } else if (Math.floor(c) !== c) {
      return 1;
    } else if (c % 10 >= 2 && c % 10 <= 4 && c % 100 < 10) {
      return 2;
    } else {
      return 3;
    }
  }

  function getLithuanianForm(c) {
    if (c === 1 || (c % 10 === 1 && c % 100 > 20)) {
      return 0;
    } else if (
      Math.floor(c) !== c ||
      (c % 10 >= 2 && c % 100 > 20) ||
      (c % 10 >= 2 && c % 100 < 10)
    ) {
      return 1;
    } else {
      return 2;
    }
  }

  function getLatvianForm(c) {
    return c % 10 === 1 && c % 100 !== 11;
  }

  // We need to make sure we support browsers that don't have
  // `Array.isArray`, so we define a fallback here.
  var isArray =
    Array.isArray ||
    function (arg) {
      return Object.prototype.toString.call(arg) === "[object Array]";
    };

  function has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  humanizeDuration.getSupportedLanguages = function getSupportedLanguages() {
    var result = [];
    for (var language in LANGUAGES) {
      if (has(LANGUAGES, language) && language !== "gr") {
        result.push(language);
      }
    }
    return result;
  };

  humanizeDuration.humanizer = humanizer;

  if (module.exports) {
    module.exports = humanizeDuration;
  } else {
    this.humanizeDuration = humanizeDuration;
  }
})();
});

const videoCardCss = ":host {\n  position: relative;\n  box-sizing: border-box;\n}\n:host *, :host *:before, :host *:after {\n  box-sizing: inherit;\n}\n\n:root {\n  --tt-color-gray-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-gray-900-25: rgba(33, 33, 33, 0.25);\n  --tt-color-gray-800-25: rgba(66, 66, 66, 0.25);\n  --tt-color-gray-700-25: rgba(97, 97, 97, 0.25);\n  --tt-color-gray-600-25: rgba(117, 117, 117, 0.25);\n  --tt-color-gray-500-25: rgba(170, 170, 170, 0.25);\n  --tt-color-gray-400-25: rgba(204, 204, 204, 0.25);\n  --tt-color-gray-300-25: rgba(221, 221, 221, 0.25);\n  --tt-color-gray-200-25: rgba(235, 235, 235, 0.25);\n  --tt-color-gray-100-25: rgba(243, 243, 243, 0.25);\n  --tt-color-gray-50-25: rgba(249, 249, 249, 0.25);\n  --tt-color-gray-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-green-gray-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-green-gray-900-25: rgba(26, 30, 27, 0.25);\n  --tt-color-green-gray-800-25: rgba(40, 47, 42, 0.25);\n  --tt-color-green-gray-700-25: rgba(63, 74, 67, 0.25);\n  --tt-color-green-gray-600-25: rgba(82, 96, 87, 0.25);\n  --tt-color-green-gray-500-25: rgba(103, 121, 110, 0.25);\n  --tt-color-green-gray-400-25: rgba(159, 173, 164, 0.25);\n  --tt-color-green-gray-300-25: rgba(211, 217, 213, 0.25);\n  --tt-color-green-gray-200-25: rgba(230, 234, 231, 0.25);\n  --tt-color-green-gray-100-25: rgba(241, 244, 242, 0.25);\n  --tt-color-green-gray-50-25: rgba(248, 250, 249, 0.25);\n  --tt-color-green-gray-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-green-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-green-900-25: rgba(4, 54, 35, 0.25);\n  --tt-color-green-800-25: rgba(11, 74, 33, 0.25);\n  --tt-color-green-700-25: rgba(15, 99, 45, 0.25);\n  --tt-color-green-600-25: rgba(19, 125, 56, 0.25);\n  --tt-color-green-500-25: rgba(23, 150, 67, 0.25);\n  --tt-color-green-400-25: rgba(30, 201, 91, 0.25);\n  --tt-color-green-300-25: rgba(100, 232, 147, 0.25);\n  --tt-color-green-200-25: rgba(166, 242, 193, 0.25);\n  --tt-color-green-100-25: rgba(215, 249, 227, 0.25);\n  --tt-color-green-50-25: rgba(237, 252, 242, 0.25);\n  --tt-color-green-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-red-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-red-900-25: rgba(57, 1, 1, 0.25);\n  --tt-color-red-800-25: rgba(83, 2, 2, 0.25);\n  --tt-color-red-700-25: rgba(111, 3, 3, 0.25);\n  --tt-color-red-600-25: rgba(140, 4, 4, 0.25);\n  --tt-color-red-500-25: rgba(199, 5, 5, 0.25);\n  --tt-color-red-400-25: rgba(250, 76, 76, 0.25);\n  --tt-color-red-300-25: rgba(252, 131, 131, 0.25);\n  --tt-color-red-200-25: rgba(253, 190, 190, 0.25);\n  --tt-color-red-100-25: rgba(254, 225, 225, 0.25);\n  --tt-color-red-50-25: rgba(255, 245, 245, 0.25);\n  --tt-color-red-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-blue-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-blue-900-25: rgba(2, 19, 54, 0.25);\n  --tt-color-blue-800-25: rgba(3, 30, 84, 0.25);\n  --tt-color-blue-700-25: rgba(4, 47, 134, 0.25);\n  --tt-color-blue-600-25: rgba(5, 61, 173, 0.25);\n  --tt-color-blue-500-25: rgba(27, 101, 248, 0.25);\n  --tt-color-blue-400-25: rgba(77, 134, 249, 0.25);\n  --tt-color-blue-300-25: rgba(176, 201, 253, 0.25);\n  --tt-color-blue-200-25: rgba(210, 225, 254, 0.25);\n  --tt-color-blue-100-25: rgba(230, 238, 254, 0.25);\n  --tt-color-blue-50-25: rgba(243, 247, 255, 0.25);\n  --tt-color-blue-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-yellow-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-yellow-900-25: rgba(30, 21, 1, 0.25);\n  --tt-color-yellow-800-25: rgba(50, 35, 1, 0.25);\n  --tt-color-yellow-700-25: rgba(85, 60, 2, 0.25);\n  --tt-color-yellow-600-25: rgba(119, 84, 3, 0.25);\n  --tt-color-yellow-500-25: rgba(154, 109, 4, 0.25);\n  --tt-color-yellow-400-25: rgba(250, 198, 76, 0.25);\n  --tt-color-yellow-300-25: rgba(252, 223, 156, 0.25);\n  --tt-color-yellow-200-25: rgba(254, 238, 200, 0.25);\n  --tt-color-yellow-100-25: rgba(254, 246, 225, 0.25);\n  --tt-color-yellow-50-25: rgba(255, 251, 243, 0.25);\n  --tt-color-yellow-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-orange-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-orange-900-25: rgba(34, 16, 2, 0.25);\n  --tt-color-orange-800-25: rgba(68, 31, 4, 0.25);\n  --tt-color-orange-700-25: rgba(102, 47, 5, 0.25);\n  --tt-color-orange-600-25: rgba(145, 67, 8, 0.25);\n  --tt-color-orange-500-25: rgba(189, 88, 10, 0.25);\n  --tt-color-orange-400-25: rgba(244, 134, 48, 0.25);\n  --tt-color-orange-300-25: rgba(247, 161, 95, 0.25);\n  --tt-color-orange-200-25: rgba(250, 198, 158, 0.25);\n  --tt-color-orange-100-25: rgba(252, 224, 202, 0.25);\n  --tt-color-orange-50-25: rgba(254, 244, 236, 0.25);\n  --tt-color-orange-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-purple-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-purple-900-25: rgba(30, 0, 56, 0.25);\n  --tt-color-purple-800-25: rgba(52, 0, 96, 0.25);\n  --tt-color-purple-700-25: rgba(79, 1, 147, 0.25);\n  --tt-color-purple-600-25: rgba(120, 1, 223, 0.25);\n  --tt-color-purple-500-25: rgba(145, 22, 254, 0.25);\n  --tt-color-purple-400-25: rgba(195, 128, 254, 0.25);\n  --tt-color-purple-300-25: rgba(217, 174, 255, 0.25);\n  --tt-color-purple-200-25: rgba(234, 209, 255, 0.25);\n  --tt-color-purple-100-25: rgba(243, 230, 255, 0.25);\n  --tt-color-purple-50-25: rgba(250, 245, 255, 0.25);\n  --tt-color-purple-0-25: rgba(255, 255, 255, 0.25);\n  --tt-color-pink-1000-25: rgba(0, 0, 0, 0.25);\n  --tt-color-pink-900-25: rgba(55, 1, 21, 0.25);\n  --tt-color-pink-800-25: rgba(94, 2, 36, 0.25);\n  --tt-color-pink-700-25: rgba(144, 4, 55, 0.25);\n  --tt-color-pink-600-25: rgba(179, 5, 69, 0.25);\n  --tt-color-pink-500-25: rgba(234, 6, 89, 0.25);\n  --tt-color-pink-400-25: rgba(250, 71, 137, 0.25);\n  --tt-color-pink-300-25: rgba(252, 126, 172, 0.25);\n  --tt-color-pink-200-25: rgba(253, 180, 207, 0.25);\n  --tt-color-pink-100-25: rgba(254, 210, 226, 0.25);\n  --tt-color-pink-50-25: rgba(254, 235, 242, 0.25);\n  --tt-color-pink-0-25: rgba(255, 255, 255, 0.25);\n}\n\n:root {\n  --tt-color-gray-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-gray-900-35: rgba(33, 33, 33, 0.35);\n  --tt-color-gray-800-35: rgba(66, 66, 66, 0.35);\n  --tt-color-gray-700-35: rgba(97, 97, 97, 0.35);\n  --tt-color-gray-600-35: rgba(117, 117, 117, 0.35);\n  --tt-color-gray-500-35: rgba(170, 170, 170, 0.35);\n  --tt-color-gray-400-35: rgba(204, 204, 204, 0.35);\n  --tt-color-gray-300-35: rgba(221, 221, 221, 0.35);\n  --tt-color-gray-200-35: rgba(235, 235, 235, 0.35);\n  --tt-color-gray-100-35: rgba(243, 243, 243, 0.35);\n  --tt-color-gray-50-35: rgba(249, 249, 249, 0.35);\n  --tt-color-gray-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-green-gray-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-green-gray-900-35: rgba(26, 30, 27, 0.35);\n  --tt-color-green-gray-800-35: rgba(40, 47, 42, 0.35);\n  --tt-color-green-gray-700-35: rgba(63, 74, 67, 0.35);\n  --tt-color-green-gray-600-35: rgba(82, 96, 87, 0.35);\n  --tt-color-green-gray-500-35: rgba(103, 121, 110, 0.35);\n  --tt-color-green-gray-400-35: rgba(159, 173, 164, 0.35);\n  --tt-color-green-gray-300-35: rgba(211, 217, 213, 0.35);\n  --tt-color-green-gray-200-35: rgba(230, 234, 231, 0.35);\n  --tt-color-green-gray-100-35: rgba(241, 244, 242, 0.35);\n  --tt-color-green-gray-50-35: rgba(248, 250, 249, 0.35);\n  --tt-color-green-gray-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-green-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-green-900-35: rgba(4, 54, 35, 0.35);\n  --tt-color-green-800-35: rgba(11, 74, 33, 0.35);\n  --tt-color-green-700-35: rgba(15, 99, 45, 0.35);\n  --tt-color-green-600-35: rgba(19, 125, 56, 0.35);\n  --tt-color-green-500-35: rgba(23, 150, 67, 0.35);\n  --tt-color-green-400-35: rgba(30, 201, 91, 0.35);\n  --tt-color-green-300-35: rgba(100, 232, 147, 0.35);\n  --tt-color-green-200-35: rgba(166, 242, 193, 0.35);\n  --tt-color-green-100-35: rgba(215, 249, 227, 0.35);\n  --tt-color-green-50-35: rgba(237, 252, 242, 0.35);\n  --tt-color-green-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-red-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-red-900-35: rgba(57, 1, 1, 0.35);\n  --tt-color-red-800-35: rgba(83, 2, 2, 0.35);\n  --tt-color-red-700-35: rgba(111, 3, 3, 0.35);\n  --tt-color-red-600-35: rgba(140, 4, 4, 0.35);\n  --tt-color-red-500-35: rgba(199, 5, 5, 0.35);\n  --tt-color-red-400-35: rgba(250, 76, 76, 0.35);\n  --tt-color-red-300-35: rgba(252, 131, 131, 0.35);\n  --tt-color-red-200-35: rgba(253, 190, 190, 0.35);\n  --tt-color-red-100-35: rgba(254, 225, 225, 0.35);\n  --tt-color-red-50-35: rgba(255, 245, 245, 0.35);\n  --tt-color-red-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-blue-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-blue-900-35: rgba(2, 19, 54, 0.35);\n  --tt-color-blue-800-35: rgba(3, 30, 84, 0.35);\n  --tt-color-blue-700-35: rgba(4, 47, 134, 0.35);\n  --tt-color-blue-600-35: rgba(5, 61, 173, 0.35);\n  --tt-color-blue-500-35: rgba(27, 101, 248, 0.35);\n  --tt-color-blue-400-35: rgba(77, 134, 249, 0.35);\n  --tt-color-blue-300-35: rgba(176, 201, 253, 0.35);\n  --tt-color-blue-200-35: rgba(210, 225, 254, 0.35);\n  --tt-color-blue-100-35: rgba(230, 238, 254, 0.35);\n  --tt-color-blue-50-35: rgba(243, 247, 255, 0.35);\n  --tt-color-blue-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-yellow-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-yellow-900-35: rgba(30, 21, 1, 0.35);\n  --tt-color-yellow-800-35: rgba(50, 35, 1, 0.35);\n  --tt-color-yellow-700-35: rgba(85, 60, 2, 0.35);\n  --tt-color-yellow-600-35: rgba(119, 84, 3, 0.35);\n  --tt-color-yellow-500-35: rgba(154, 109, 4, 0.35);\n  --tt-color-yellow-400-35: rgba(250, 198, 76, 0.35);\n  --tt-color-yellow-300-35: rgba(252, 223, 156, 0.35);\n  --tt-color-yellow-200-35: rgba(254, 238, 200, 0.35);\n  --tt-color-yellow-100-35: rgba(254, 246, 225, 0.35);\n  --tt-color-yellow-50-35: rgba(255, 251, 243, 0.35);\n  --tt-color-yellow-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-orange-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-orange-900-35: rgba(34, 16, 2, 0.35);\n  --tt-color-orange-800-35: rgba(68, 31, 4, 0.35);\n  --tt-color-orange-700-35: rgba(102, 47, 5, 0.35);\n  --tt-color-orange-600-35: rgba(145, 67, 8, 0.35);\n  --tt-color-orange-500-35: rgba(189, 88, 10, 0.35);\n  --tt-color-orange-400-35: rgba(244, 134, 48, 0.35);\n  --tt-color-orange-300-35: rgba(247, 161, 95, 0.35);\n  --tt-color-orange-200-35: rgba(250, 198, 158, 0.35);\n  --tt-color-orange-100-35: rgba(252, 224, 202, 0.35);\n  --tt-color-orange-50-35: rgba(254, 244, 236, 0.35);\n  --tt-color-orange-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-purple-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-purple-900-35: rgba(30, 0, 56, 0.35);\n  --tt-color-purple-800-35: rgba(52, 0, 96, 0.35);\n  --tt-color-purple-700-35: rgba(79, 1, 147, 0.35);\n  --tt-color-purple-600-35: rgba(120, 1, 223, 0.35);\n  --tt-color-purple-500-35: rgba(145, 22, 254, 0.35);\n  --tt-color-purple-400-35: rgba(195, 128, 254, 0.35);\n  --tt-color-purple-300-35: rgba(217, 174, 255, 0.35);\n  --tt-color-purple-200-35: rgba(234, 209, 255, 0.35);\n  --tt-color-purple-100-35: rgba(243, 230, 255, 0.35);\n  --tt-color-purple-50-35: rgba(250, 245, 255, 0.35);\n  --tt-color-purple-0-35: rgba(255, 255, 255, 0.35);\n  --tt-color-pink-1000-35: rgba(0, 0, 0, 0.35);\n  --tt-color-pink-900-35: rgba(55, 1, 21, 0.35);\n  --tt-color-pink-800-35: rgba(94, 2, 36, 0.35);\n  --tt-color-pink-700-35: rgba(144, 4, 55, 0.35);\n  --tt-color-pink-600-35: rgba(179, 5, 69, 0.35);\n  --tt-color-pink-500-35: rgba(234, 6, 89, 0.35);\n  --tt-color-pink-400-35: rgba(250, 71, 137, 0.35);\n  --tt-color-pink-300-35: rgba(252, 126, 172, 0.35);\n  --tt-color-pink-200-35: rgba(253, 180, 207, 0.35);\n  --tt-color-pink-100-35: rgba(254, 210, 226, 0.35);\n  --tt-color-pink-50-35: rgba(254, 235, 242, 0.35);\n  --tt-color-pink-0-35: rgba(255, 255, 255, 0.35);\n}\n\n:root {\n  --tt-color-gray-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-gray-900-50: rgba(33, 33, 33, 0.5);\n  --tt-color-gray-800-50: rgba(66, 66, 66, 0.5);\n  --tt-color-gray-700-50: rgba(97, 97, 97, 0.5);\n  --tt-color-gray-600-50: rgba(117, 117, 117, 0.5);\n  --tt-color-gray-500-50: rgba(170, 170, 170, 0.5);\n  --tt-color-gray-400-50: rgba(204, 204, 204, 0.5);\n  --tt-color-gray-300-50: rgba(221, 221, 221, 0.5);\n  --tt-color-gray-200-50: rgba(235, 235, 235, 0.5);\n  --tt-color-gray-100-50: rgba(243, 243, 243, 0.5);\n  --tt-color-gray-50-50: rgba(249, 249, 249, 0.5);\n  --tt-color-gray-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-green-gray-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-green-gray-900-50: rgba(26, 30, 27, 0.5);\n  --tt-color-green-gray-800-50: rgba(40, 47, 42, 0.5);\n  --tt-color-green-gray-700-50: rgba(63, 74, 67, 0.5);\n  --tt-color-green-gray-600-50: rgba(82, 96, 87, 0.5);\n  --tt-color-green-gray-500-50: rgba(103, 121, 110, 0.5);\n  --tt-color-green-gray-400-50: rgba(159, 173, 164, 0.5);\n  --tt-color-green-gray-300-50: rgba(211, 217, 213, 0.5);\n  --tt-color-green-gray-200-50: rgba(230, 234, 231, 0.5);\n  --tt-color-green-gray-100-50: rgba(241, 244, 242, 0.5);\n  --tt-color-green-gray-50-50: rgba(248, 250, 249, 0.5);\n  --tt-color-green-gray-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-green-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-green-900-50: rgba(4, 54, 35, 0.5);\n  --tt-color-green-800-50: rgba(11, 74, 33, 0.5);\n  --tt-color-green-700-50: rgba(15, 99, 45, 0.5);\n  --tt-color-green-600-50: rgba(19, 125, 56, 0.5);\n  --tt-color-green-500-50: rgba(23, 150, 67, 0.5);\n  --tt-color-green-400-50: rgba(30, 201, 91, 0.5);\n  --tt-color-green-300-50: rgba(100, 232, 147, 0.5);\n  --tt-color-green-200-50: rgba(166, 242, 193, 0.5);\n  --tt-color-green-100-50: rgba(215, 249, 227, 0.5);\n  --tt-color-green-50-50: rgba(237, 252, 242, 0.5);\n  --tt-color-green-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-red-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-red-900-50: rgba(57, 1, 1, 0.5);\n  --tt-color-red-800-50: rgba(83, 2, 2, 0.5);\n  --tt-color-red-700-50: rgba(111, 3, 3, 0.5);\n  --tt-color-red-600-50: rgba(140, 4, 4, 0.5);\n  --tt-color-red-500-50: rgba(199, 5, 5, 0.5);\n  --tt-color-red-400-50: rgba(250, 76, 76, 0.5);\n  --tt-color-red-300-50: rgba(252, 131, 131, 0.5);\n  --tt-color-red-200-50: rgba(253, 190, 190, 0.5);\n  --tt-color-red-100-50: rgba(254, 225, 225, 0.5);\n  --tt-color-red-50-50: rgba(255, 245, 245, 0.5);\n  --tt-color-red-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-blue-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-blue-900-50: rgba(2, 19, 54, 0.5);\n  --tt-color-blue-800-50: rgba(3, 30, 84, 0.5);\n  --tt-color-blue-700-50: rgba(4, 47, 134, 0.5);\n  --tt-color-blue-600-50: rgba(5, 61, 173, 0.5);\n  --tt-color-blue-500-50: rgba(27, 101, 248, 0.5);\n  --tt-color-blue-400-50: rgba(77, 134, 249, 0.5);\n  --tt-color-blue-300-50: rgba(176, 201, 253, 0.5);\n  --tt-color-blue-200-50: rgba(210, 225, 254, 0.5);\n  --tt-color-blue-100-50: rgba(230, 238, 254, 0.5);\n  --tt-color-blue-50-50: rgba(243, 247, 255, 0.5);\n  --tt-color-blue-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-yellow-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-yellow-900-50: rgba(30, 21, 1, 0.5);\n  --tt-color-yellow-800-50: rgba(50, 35, 1, 0.5);\n  --tt-color-yellow-700-50: rgba(85, 60, 2, 0.5);\n  --tt-color-yellow-600-50: rgba(119, 84, 3, 0.5);\n  --tt-color-yellow-500-50: rgba(154, 109, 4, 0.5);\n  --tt-color-yellow-400-50: rgba(250, 198, 76, 0.5);\n  --tt-color-yellow-300-50: rgba(252, 223, 156, 0.5);\n  --tt-color-yellow-200-50: rgba(254, 238, 200, 0.5);\n  --tt-color-yellow-100-50: rgba(254, 246, 225, 0.5);\n  --tt-color-yellow-50-50: rgba(255, 251, 243, 0.5);\n  --tt-color-yellow-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-orange-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-orange-900-50: rgba(34, 16, 2, 0.5);\n  --tt-color-orange-800-50: rgba(68, 31, 4, 0.5);\n  --tt-color-orange-700-50: rgba(102, 47, 5, 0.5);\n  --tt-color-orange-600-50: rgba(145, 67, 8, 0.5);\n  --tt-color-orange-500-50: rgba(189, 88, 10, 0.5);\n  --tt-color-orange-400-50: rgba(244, 134, 48, 0.5);\n  --tt-color-orange-300-50: rgba(247, 161, 95, 0.5);\n  --tt-color-orange-200-50: rgba(250, 198, 158, 0.5);\n  --tt-color-orange-100-50: rgba(252, 224, 202, 0.5);\n  --tt-color-orange-50-50: rgba(254, 244, 236, 0.5);\n  --tt-color-orange-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-purple-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-purple-900-50: rgba(30, 0, 56, 0.5);\n  --tt-color-purple-800-50: rgba(52, 0, 96, 0.5);\n  --tt-color-purple-700-50: rgba(79, 1, 147, 0.5);\n  --tt-color-purple-600-50: rgba(120, 1, 223, 0.5);\n  --tt-color-purple-500-50: rgba(145, 22, 254, 0.5);\n  --tt-color-purple-400-50: rgba(195, 128, 254, 0.5);\n  --tt-color-purple-300-50: rgba(217, 174, 255, 0.5);\n  --tt-color-purple-200-50: rgba(234, 209, 255, 0.5);\n  --tt-color-purple-100-50: rgba(243, 230, 255, 0.5);\n  --tt-color-purple-50-50: rgba(250, 245, 255, 0.5);\n  --tt-color-purple-0-50: rgba(255, 255, 255, 0.5);\n  --tt-color-pink-1000-50: rgba(0, 0, 0, 0.5);\n  --tt-color-pink-900-50: rgba(55, 1, 21, 0.5);\n  --tt-color-pink-800-50: rgba(94, 2, 36, 0.5);\n  --tt-color-pink-700-50: rgba(144, 4, 55, 0.5);\n  --tt-color-pink-600-50: rgba(179, 5, 69, 0.5);\n  --tt-color-pink-500-50: rgba(234, 6, 89, 0.5);\n  --tt-color-pink-400-50: rgba(250, 71, 137, 0.5);\n  --tt-color-pink-300-50: rgba(252, 126, 172, 0.5);\n  --tt-color-pink-200-50: rgba(253, 180, 207, 0.5);\n  --tt-color-pink-100-50: rgba(254, 210, 226, 0.5);\n  --tt-color-pink-50-50: rgba(254, 235, 242, 0.5);\n  --tt-color-pink-0-50: rgba(255, 255, 255, 0.5);\n}\n\n:root {\n  --tt-color-gray-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-gray-900-65: rgba(33, 33, 33, 0.65);\n  --tt-color-gray-800-65: rgba(66, 66, 66, 0.65);\n  --tt-color-gray-700-65: rgba(97, 97, 97, 0.65);\n  --tt-color-gray-600-65: rgba(117, 117, 117, 0.65);\n  --tt-color-gray-500-65: rgba(170, 170, 170, 0.65);\n  --tt-color-gray-400-65: rgba(204, 204, 204, 0.65);\n  --tt-color-gray-300-65: rgba(221, 221, 221, 0.65);\n  --tt-color-gray-200-65: rgba(235, 235, 235, 0.65);\n  --tt-color-gray-100-65: rgba(243, 243, 243, 0.65);\n  --tt-color-gray-50-65: rgba(249, 249, 249, 0.65);\n  --tt-color-gray-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-green-gray-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-green-gray-900-65: rgba(26, 30, 27, 0.65);\n  --tt-color-green-gray-800-65: rgba(40, 47, 42, 0.65);\n  --tt-color-green-gray-700-65: rgba(63, 74, 67, 0.65);\n  --tt-color-green-gray-600-65: rgba(82, 96, 87, 0.65);\n  --tt-color-green-gray-500-65: rgba(103, 121, 110, 0.65);\n  --tt-color-green-gray-400-65: rgba(159, 173, 164, 0.65);\n  --tt-color-green-gray-300-65: rgba(211, 217, 213, 0.65);\n  --tt-color-green-gray-200-65: rgba(230, 234, 231, 0.65);\n  --tt-color-green-gray-100-65: rgba(241, 244, 242, 0.65);\n  --tt-color-green-gray-50-65: rgba(248, 250, 249, 0.65);\n  --tt-color-green-gray-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-green-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-green-900-65: rgba(4, 54, 35, 0.65);\n  --tt-color-green-800-65: rgba(11, 74, 33, 0.65);\n  --tt-color-green-700-65: rgba(15, 99, 45, 0.65);\n  --tt-color-green-600-65: rgba(19, 125, 56, 0.65);\n  --tt-color-green-500-65: rgba(23, 150, 67, 0.65);\n  --tt-color-green-400-65: rgba(30, 201, 91, 0.65);\n  --tt-color-green-300-65: rgba(100, 232, 147, 0.65);\n  --tt-color-green-200-65: rgba(166, 242, 193, 0.65);\n  --tt-color-green-100-65: rgba(215, 249, 227, 0.65);\n  --tt-color-green-50-65: rgba(237, 252, 242, 0.65);\n  --tt-color-green-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-red-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-red-900-65: rgba(57, 1, 1, 0.65);\n  --tt-color-red-800-65: rgba(83, 2, 2, 0.65);\n  --tt-color-red-700-65: rgba(111, 3, 3, 0.65);\n  --tt-color-red-600-65: rgba(140, 4, 4, 0.65);\n  --tt-color-red-500-65: rgba(199, 5, 5, 0.65);\n  --tt-color-red-400-65: rgba(250, 76, 76, 0.65);\n  --tt-color-red-300-65: rgba(252, 131, 131, 0.65);\n  --tt-color-red-200-65: rgba(253, 190, 190, 0.65);\n  --tt-color-red-100-65: rgba(254, 225, 225, 0.65);\n  --tt-color-red-50-65: rgba(255, 245, 245, 0.65);\n  --tt-color-red-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-blue-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-blue-900-65: rgba(2, 19, 54, 0.65);\n  --tt-color-blue-800-65: rgba(3, 30, 84, 0.65);\n  --tt-color-blue-700-65: rgba(4, 47, 134, 0.65);\n  --tt-color-blue-600-65: rgba(5, 61, 173, 0.65);\n  --tt-color-blue-500-65: rgba(27, 101, 248, 0.65);\n  --tt-color-blue-400-65: rgba(77, 134, 249, 0.65);\n  --tt-color-blue-300-65: rgba(176, 201, 253, 0.65);\n  --tt-color-blue-200-65: rgba(210, 225, 254, 0.65);\n  --tt-color-blue-100-65: rgba(230, 238, 254, 0.65);\n  --tt-color-blue-50-65: rgba(243, 247, 255, 0.65);\n  --tt-color-blue-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-yellow-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-yellow-900-65: rgba(30, 21, 1, 0.65);\n  --tt-color-yellow-800-65: rgba(50, 35, 1, 0.65);\n  --tt-color-yellow-700-65: rgba(85, 60, 2, 0.65);\n  --tt-color-yellow-600-65: rgba(119, 84, 3, 0.65);\n  --tt-color-yellow-500-65: rgba(154, 109, 4, 0.65);\n  --tt-color-yellow-400-65: rgba(250, 198, 76, 0.65);\n  --tt-color-yellow-300-65: rgba(252, 223, 156, 0.65);\n  --tt-color-yellow-200-65: rgba(254, 238, 200, 0.65);\n  --tt-color-yellow-100-65: rgba(254, 246, 225, 0.65);\n  --tt-color-yellow-50-65: rgba(255, 251, 243, 0.65);\n  --tt-color-yellow-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-orange-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-orange-900-65: rgba(34, 16, 2, 0.65);\n  --tt-color-orange-800-65: rgba(68, 31, 4, 0.65);\n  --tt-color-orange-700-65: rgba(102, 47, 5, 0.65);\n  --tt-color-orange-600-65: rgba(145, 67, 8, 0.65);\n  --tt-color-orange-500-65: rgba(189, 88, 10, 0.65);\n  --tt-color-orange-400-65: rgba(244, 134, 48, 0.65);\n  --tt-color-orange-300-65: rgba(247, 161, 95, 0.65);\n  --tt-color-orange-200-65: rgba(250, 198, 158, 0.65);\n  --tt-color-orange-100-65: rgba(252, 224, 202, 0.65);\n  --tt-color-orange-50-65: rgba(254, 244, 236, 0.65);\n  --tt-color-orange-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-purple-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-purple-900-65: rgba(30, 0, 56, 0.65);\n  --tt-color-purple-800-65: rgba(52, 0, 96, 0.65);\n  --tt-color-purple-700-65: rgba(79, 1, 147, 0.65);\n  --tt-color-purple-600-65: rgba(120, 1, 223, 0.65);\n  --tt-color-purple-500-65: rgba(145, 22, 254, 0.65);\n  --tt-color-purple-400-65: rgba(195, 128, 254, 0.65);\n  --tt-color-purple-300-65: rgba(217, 174, 255, 0.65);\n  --tt-color-purple-200-65: rgba(234, 209, 255, 0.65);\n  --tt-color-purple-100-65: rgba(243, 230, 255, 0.65);\n  --tt-color-purple-50-65: rgba(250, 245, 255, 0.65);\n  --tt-color-purple-0-65: rgba(255, 255, 255, 0.65);\n  --tt-color-pink-1000-65: rgba(0, 0, 0, 0.65);\n  --tt-color-pink-900-65: rgba(55, 1, 21, 0.65);\n  --tt-color-pink-800-65: rgba(94, 2, 36, 0.65);\n  --tt-color-pink-700-65: rgba(144, 4, 55, 0.65);\n  --tt-color-pink-600-65: rgba(179, 5, 69, 0.65);\n  --tt-color-pink-500-65: rgba(234, 6, 89, 0.65);\n  --tt-color-pink-400-65: rgba(250, 71, 137, 0.65);\n  --tt-color-pink-300-65: rgba(252, 126, 172, 0.65);\n  --tt-color-pink-200-65: rgba(253, 180, 207, 0.65);\n  --tt-color-pink-100-65: rgba(254, 210, 226, 0.65);\n  --tt-color-pink-50-65: rgba(254, 235, 242, 0.65);\n  --tt-color-pink-0-65: rgba(255, 255, 255, 0.65);\n}\n\n:root {\n  --tt-color-gray-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-gray-900-75: rgba(33, 33, 33, 0.75);\n  --tt-color-gray-800-75: rgba(66, 66, 66, 0.75);\n  --tt-color-gray-700-75: rgba(97, 97, 97, 0.75);\n  --tt-color-gray-600-75: rgba(117, 117, 117, 0.75);\n  --tt-color-gray-500-75: rgba(170, 170, 170, 0.75);\n  --tt-color-gray-400-75: rgba(204, 204, 204, 0.75);\n  --tt-color-gray-300-75: rgba(221, 221, 221, 0.75);\n  --tt-color-gray-200-75: rgba(235, 235, 235, 0.75);\n  --tt-color-gray-100-75: rgba(243, 243, 243, 0.75);\n  --tt-color-gray-50-75: rgba(249, 249, 249, 0.75);\n  --tt-color-gray-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-green-gray-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-green-gray-900-75: rgba(26, 30, 27, 0.75);\n  --tt-color-green-gray-800-75: rgba(40, 47, 42, 0.75);\n  --tt-color-green-gray-700-75: rgba(63, 74, 67, 0.75);\n  --tt-color-green-gray-600-75: rgba(82, 96, 87, 0.75);\n  --tt-color-green-gray-500-75: rgba(103, 121, 110, 0.75);\n  --tt-color-green-gray-400-75: rgba(159, 173, 164, 0.75);\n  --tt-color-green-gray-300-75: rgba(211, 217, 213, 0.75);\n  --tt-color-green-gray-200-75: rgba(230, 234, 231, 0.75);\n  --tt-color-green-gray-100-75: rgba(241, 244, 242, 0.75);\n  --tt-color-green-gray-50-75: rgba(248, 250, 249, 0.75);\n  --tt-color-green-gray-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-green-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-green-900-75: rgba(4, 54, 35, 0.75);\n  --tt-color-green-800-75: rgba(11, 74, 33, 0.75);\n  --tt-color-green-700-75: rgba(15, 99, 45, 0.75);\n  --tt-color-green-600-75: rgba(19, 125, 56, 0.75);\n  --tt-color-green-500-75: rgba(23, 150, 67, 0.75);\n  --tt-color-green-400-75: rgba(30, 201, 91, 0.75);\n  --tt-color-green-300-75: rgba(100, 232, 147, 0.75);\n  --tt-color-green-200-75: rgba(166, 242, 193, 0.75);\n  --tt-color-green-100-75: rgba(215, 249, 227, 0.75);\n  --tt-color-green-50-75: rgba(237, 252, 242, 0.75);\n  --tt-color-green-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-red-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-red-900-75: rgba(57, 1, 1, 0.75);\n  --tt-color-red-800-75: rgba(83, 2, 2, 0.75);\n  --tt-color-red-700-75: rgba(111, 3, 3, 0.75);\n  --tt-color-red-600-75: rgba(140, 4, 4, 0.75);\n  --tt-color-red-500-75: rgba(199, 5, 5, 0.75);\n  --tt-color-red-400-75: rgba(250, 76, 76, 0.75);\n  --tt-color-red-300-75: rgba(252, 131, 131, 0.75);\n  --tt-color-red-200-75: rgba(253, 190, 190, 0.75);\n  --tt-color-red-100-75: rgba(254, 225, 225, 0.75);\n  --tt-color-red-50-75: rgba(255, 245, 245, 0.75);\n  --tt-color-red-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-blue-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-blue-900-75: rgba(2, 19, 54, 0.75);\n  --tt-color-blue-800-75: rgba(3, 30, 84, 0.75);\n  --tt-color-blue-700-75: rgba(4, 47, 134, 0.75);\n  --tt-color-blue-600-75: rgba(5, 61, 173, 0.75);\n  --tt-color-blue-500-75: rgba(27, 101, 248, 0.75);\n  --tt-color-blue-400-75: rgba(77, 134, 249, 0.75);\n  --tt-color-blue-300-75: rgba(176, 201, 253, 0.75);\n  --tt-color-blue-200-75: rgba(210, 225, 254, 0.75);\n  --tt-color-blue-100-75: rgba(230, 238, 254, 0.75);\n  --tt-color-blue-50-75: rgba(243, 247, 255, 0.75);\n  --tt-color-blue-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-yellow-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-yellow-900-75: rgba(30, 21, 1, 0.75);\n  --tt-color-yellow-800-75: rgba(50, 35, 1, 0.75);\n  --tt-color-yellow-700-75: rgba(85, 60, 2, 0.75);\n  --tt-color-yellow-600-75: rgba(119, 84, 3, 0.75);\n  --tt-color-yellow-500-75: rgba(154, 109, 4, 0.75);\n  --tt-color-yellow-400-75: rgba(250, 198, 76, 0.75);\n  --tt-color-yellow-300-75: rgba(252, 223, 156, 0.75);\n  --tt-color-yellow-200-75: rgba(254, 238, 200, 0.75);\n  --tt-color-yellow-100-75: rgba(254, 246, 225, 0.75);\n  --tt-color-yellow-50-75: rgba(255, 251, 243, 0.75);\n  --tt-color-yellow-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-orange-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-orange-900-75: rgba(34, 16, 2, 0.75);\n  --tt-color-orange-800-75: rgba(68, 31, 4, 0.75);\n  --tt-color-orange-700-75: rgba(102, 47, 5, 0.75);\n  --tt-color-orange-600-75: rgba(145, 67, 8, 0.75);\n  --tt-color-orange-500-75: rgba(189, 88, 10, 0.75);\n  --tt-color-orange-400-75: rgba(244, 134, 48, 0.75);\n  --tt-color-orange-300-75: rgba(247, 161, 95, 0.75);\n  --tt-color-orange-200-75: rgba(250, 198, 158, 0.75);\n  --tt-color-orange-100-75: rgba(252, 224, 202, 0.75);\n  --tt-color-orange-50-75: rgba(254, 244, 236, 0.75);\n  --tt-color-orange-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-purple-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-purple-900-75: rgba(30, 0, 56, 0.75);\n  --tt-color-purple-800-75: rgba(52, 0, 96, 0.75);\n  --tt-color-purple-700-75: rgba(79, 1, 147, 0.75);\n  --tt-color-purple-600-75: rgba(120, 1, 223, 0.75);\n  --tt-color-purple-500-75: rgba(145, 22, 254, 0.75);\n  --tt-color-purple-400-75: rgba(195, 128, 254, 0.75);\n  --tt-color-purple-300-75: rgba(217, 174, 255, 0.75);\n  --tt-color-purple-200-75: rgba(234, 209, 255, 0.75);\n  --tt-color-purple-100-75: rgba(243, 230, 255, 0.75);\n  --tt-color-purple-50-75: rgba(250, 245, 255, 0.75);\n  --tt-color-purple-0-75: rgba(255, 255, 255, 0.75);\n  --tt-color-pink-1000-75: rgba(0, 0, 0, 0.75);\n  --tt-color-pink-900-75: rgba(55, 1, 21, 0.75);\n  --tt-color-pink-800-75: rgba(94, 2, 36, 0.75);\n  --tt-color-pink-700-75: rgba(144, 4, 55, 0.75);\n  --tt-color-pink-600-75: rgba(179, 5, 69, 0.75);\n  --tt-color-pink-500-75: rgba(234, 6, 89, 0.75);\n  --tt-color-pink-400-75: rgba(250, 71, 137, 0.75);\n  --tt-color-pink-300-75: rgba(252, 126, 172, 0.75);\n  --tt-color-pink-200-75: rgba(253, 180, 207, 0.75);\n  --tt-color-pink-100-75: rgba(254, 210, 226, 0.75);\n  --tt-color-pink-50-75: rgba(254, 235, 242, 0.75);\n  --tt-color-pink-0-75: rgba(255, 255, 255, 0.75);\n}\n\n:root {\n  --tt-color-white: #fff;\n  --tt-color-black: #000;\n  --tt-color-gray-1000: #000;\n  --tt-color-gray-900: #212121;\n  --tt-color-gray-800: #424242;\n  --tt-color-gray-700: #616161;\n  --tt-color-gray-600: #757575;\n  --tt-color-gray-500: #aaaaaa;\n  --tt-color-gray-400: #cccccc;\n  --tt-color-gray-300: #dddddd;\n  --tt-color-gray-200: #ebebeb;\n  --tt-color-gray-100: #f3f3f3;\n  --tt-color-gray-50: #f9f9f9;\n  --tt-color-gray-0: #fff;\n  --tt-color-green-gray-1000: #000;\n  --tt-color-green-gray-900: #1a1e1b;\n  --tt-color-green-gray-800: #282f2a;\n  --tt-color-green-gray-700: #3f4a43;\n  --tt-color-green-gray-600: #526057;\n  --tt-color-green-gray-500: #67796e;\n  --tt-color-green-gray-400: #9fada4;\n  --tt-color-green-gray-300: #d3d9d5;\n  --tt-color-green-gray-200: #e6eae7;\n  --tt-color-green-gray-100: #f1f4f2;\n  --tt-color-green-gray-50: #f8faf9;\n  --tt-color-green-gray-0: #fff;\n  --tt-color-green-1000: #000;\n  --tt-color-green-900: #043623;\n  --tt-color-green-800: #0b4a21;\n  --tt-color-green-700: #0f632d;\n  --tt-color-green-600: #137d38;\n  --tt-color-green-500: #179643;\n  --tt-color-green-400: #1ec95b;\n  --tt-color-green-300: #64e893;\n  --tt-color-green-200: #a6f2c1;\n  --tt-color-green-100: #d7f9e3;\n  --tt-color-green-50: #edfcf2;\n  --tt-color-green-0: #fff;\n  --tt-color-red-1000: #000;\n  --tt-color-red-900: #390101;\n  --tt-color-red-800: #530202;\n  --tt-color-red-700: #6f0303;\n  --tt-color-red-600: #8c0404;\n  --tt-color-red-500: #c70505;\n  --tt-color-red-400: #fa4c4c;\n  --tt-color-red-300: #fc8383;\n  --tt-color-red-200: #fdbebe;\n  --tt-color-red-100: #fee1e1;\n  --tt-color-red-50: #fff5f5;\n  --tt-color-red-0: #fff;\n  --tt-color-blue-1000: #000;\n  --tt-color-blue-900: #021336;\n  --tt-color-blue-800: #031e54;\n  --tt-color-blue-700: #042f86;\n  --tt-color-blue-600: #053dad;\n  --tt-color-blue-500: #1b65f8;\n  --tt-color-blue-400: #4d86f9;\n  --tt-color-blue-300: #b0c9fd;\n  --tt-color-blue-200: #d2e1fe;\n  --tt-color-blue-100: #e6eefe;\n  --tt-color-blue-50: #f3f7ff;\n  --tt-color-blue-0: #fff;\n  --tt-color-yellow-1000: #000;\n  --tt-color-yellow-900: #1e1501;\n  --tt-color-yellow-800: #322301;\n  --tt-color-yellow-700: #553c02;\n  --tt-color-yellow-600: #775403;\n  --tt-color-yellow-500: #9a6d04;\n  --tt-color-yellow-400: #fac64c;\n  --tt-color-yellow-300: #fcdf9c;\n  --tt-color-yellow-200: #feeec8;\n  --tt-color-yellow-100: #fef6e1;\n  --tt-color-yellow-50: #fffbf3;\n  --tt-color-yellow-0: #fff;\n  --tt-color-orange-1000: #000;\n  --tt-color-orange-900: #221002;\n  --tt-color-orange-800: #441f04;\n  --tt-color-orange-700: #662f05;\n  --tt-color-orange-600: #914308;\n  --tt-color-orange-500: #bd580a;\n  --tt-color-orange-400: #f48630;\n  --tt-color-orange-300: #f7a15f;\n  --tt-color-orange-200: #fac69e;\n  --tt-color-orange-100: #fce0ca;\n  --tt-color-orange-50: #fef4ec;\n  --tt-color-orange-0: #fff;\n  --tt-color-purple-1000: #000;\n  --tt-color-purple-900: #1e0038;\n  --tt-color-purple-800: #340060;\n  --tt-color-purple-700: #4f0193;\n  --tt-color-purple-600: #7801df;\n  --tt-color-purple-500: #9116fe;\n  --tt-color-purple-400: #c380fe;\n  --tt-color-purple-300: #d9aeff;\n  --tt-color-purple-200: #ead1ff;\n  --tt-color-purple-100: #f3e6ff;\n  --tt-color-purple-50: #faf5ff;\n  --tt-color-purple-0: #fff;\n  --tt-color-pink-1000: #000;\n  --tt-color-pink-900: #370115;\n  --tt-color-pink-800: #5e0224;\n  --tt-color-pink-700: #900437;\n  --tt-color-pink-600: #b30545;\n  --tt-color-pink-500: #ea0659;\n  --tt-color-pink-400: #fa4789;\n  --tt-color-pink-300: #fc7eac;\n  --tt-color-pink-200: #fdb4cf;\n  --tt-color-pink-100: #fed2e2;\n  --tt-color-pink-50: #feebf2;\n  --tt-color-pink-0: #fff;\n  --tt-color-spring-green: var(--tt-color-green-400);\n  --tt-color-racing-green: var(--tt-color-green-900);\n  --tt-color-rec-red: var(--tt-color-red-400);\n  --tt-color-form-control-background: var(--tt-color-gray-50);\n  --tt-color-form-control-hover-background: var(--tt-color-gray-200);\n  --tt-color-form-control-label: var(--tt-color-gray-900);\n  --tt-color-form-control-disabled-label: var(--tt-color-gray-400);\n  --tt-color-form-control-placeholder: var(--tt-color-gray-600);\n}\n\n:export {\n  color-white: #fff;\n  color-black: #000;\n  color-gray-1000: #000;\n  color-gray-900: #212121;\n  color-gray-800: #424242;\n  color-gray-700: #616161;\n  color-gray-600: #757575;\n  color-gray-500: #aaaaaa;\n  color-gray-400: #cccccc;\n  color-gray-300: #dddddd;\n  color-gray-200: #ebebeb;\n  color-gray-100: #f3f3f3;\n  color-gray-50: #f9f9f9;\n  color-gray-0: #fff;\n  color-green-gray-1000: #000;\n  color-green-gray-900: #1a1e1b;\n  color-green-gray-800: #282f2a;\n  color-green-gray-700: #3f4a43;\n  color-green-gray-600: #526057;\n  color-green-gray-500: #67796e;\n  color-green-gray-400: #9fada4;\n  color-green-gray-300: #d3d9d5;\n  color-green-gray-200: #e6eae7;\n  color-green-gray-100: #f1f4f2;\n  color-green-gray-50: #f8faf9;\n  color-green-gray-0: #fff;\n  color-green-1000: #000;\n  color-green-900: #043623;\n  color-green-800: #0b4a21;\n  color-green-700: #0f632d;\n  color-green-600: #137d38;\n  color-green-500: #179643;\n  color-green-400: #1ec95b;\n  color-green-300: #64e893;\n  color-green-200: #a6f2c1;\n  color-green-100: #d7f9e3;\n  color-green-50: #edfcf2;\n  color-green-0: #fff;\n  color-red-1000: #000;\n  color-red-900: #390101;\n  color-red-800: #530202;\n  color-red-700: #6f0303;\n  color-red-600: #8c0404;\n  color-red-500: #c70505;\n  color-red-400: #fa4c4c;\n  color-red-300: #fc8383;\n  color-red-200: #fdbebe;\n  color-red-100: #fee1e1;\n  color-red-50: #fff5f5;\n  color-red-0: #fff;\n  color-blue-1000: #000;\n  color-blue-900: #021336;\n  color-blue-800: #031e54;\n  color-blue-700: #042f86;\n  color-blue-600: #053dad;\n  color-blue-500: #1b65f8;\n  color-blue-400: #4d86f9;\n  color-blue-300: #b0c9fd;\n  color-blue-200: #d2e1fe;\n  color-blue-100: #e6eefe;\n  color-blue-50: #f3f7ff;\n  color-blue-0: #fff;\n  color-yellow-1000: #000;\n  color-yellow-900: #1e1501;\n  color-yellow-800: #322301;\n  color-yellow-700: #553c02;\n  color-yellow-600: #775403;\n  color-yellow-500: #9a6d04;\n  color-yellow-400: #fac64c;\n  color-yellow-300: #fcdf9c;\n  color-yellow-200: #feeec8;\n  color-yellow-100: #fef6e1;\n  color-yellow-50: #fffbf3;\n  color-yellow-0: #fff;\n  color-orange-1000: #000;\n  color-orange-900: #221002;\n  color-orange-800: #441f04;\n  color-orange-700: #662f05;\n  color-orange-600: #914308;\n  color-orange-500: #bd580a;\n  color-orange-400: #f48630;\n  color-orange-300: #f7a15f;\n  color-orange-200: #fac69e;\n  color-orange-100: #fce0ca;\n  color-orange-50: #fef4ec;\n  color-orange-0: #fff;\n  color-purple-1000: #000;\n  color-purple-900: #1e0038;\n  color-purple-800: #340060;\n  color-purple-700: #4f0193;\n  color-purple-600: #7801df;\n  color-purple-500: #9116fe;\n  color-purple-400: #c380fe;\n  color-purple-300: #d9aeff;\n  color-purple-200: #ead1ff;\n  color-purple-100: #f3e6ff;\n  color-purple-50: #faf5ff;\n  color-purple-0: #fff;\n  color-pink-1000: #000;\n  color-pink-900: #370115;\n  color-pink-800: #5e0224;\n  color-pink-700: #900437;\n  color-pink-600: #b30545;\n  color-pink-500: #ea0659;\n  color-pink-400: #fa4789;\n  color-pink-300: #fc7eac;\n  color-pink-200: #fdb4cf;\n  color-pink-100: #fed2e2;\n  color-pink-50: #feebf2;\n  color-pink-0: #fff;\n}\n\n:host {\n  min-width: 175px;\n  display: block;\n}\n\n.media-card-container {\n  aspect-ratio: var(--aspect-ratio);\n  display: grid;\n  isolation: isolate;\n  width: 100%;\n}\n\n/* prettier-ignore */\n/**\n * @prop --aspect-ratio: Controls the video-card's aspect-ratio\n */\n:host {\n  min-width: 175px;\n  display: block;\n  --aspect-ratio: 16/11;\n}\n\n:host(:where([selectable=\"true\"], [selectable]:not([selectable=\"false\"]))) {\n  cursor: pointer;\n}\n:host(:where([selectable=\"true\"], [selectable]:not([selectable=\"false\"]))) * {\n  user-select: none;\n  pointer-events: none;\n}\n\n.media-card-container {\n  position: relative;\n}\n\n.media-card-container > * {\n  position: absolute;\n  inset: 0;\n}\n\n.thumbnail-stack {\n  position: absolute;\n  width: 95%;\n  left: 2.5%;\n  top: calc(-2.5% - 1px);\n  height: calc(2.5% + 1px - max(1px, 1%));\n  background-color: #616161;\n  background-repeat: no-repeat;\n  background-size: cover;\n}\n.thumbnail-stack::after {\n  content: \"\";\n  position: absolute;\n  inset: 0;\n  background-color: black;\n  opacity: 40%;\n}\n\n.image {\n  z-index: 1;\n  background-color: #212121;\n  background-size: cover;\n  background-repeat: no-repeat;\n  background-position: left center;\n  overflow: hidden;\n}\n\n.preview {\n  background-color: rgba(40, 47, 42, 0.1);\n  background-size: 102% auto;\n  background-position-y: var(--offset-y, 0);\n  background-position-x: center;\n  backdrop-filter: blur(8px);\n  opacity: 1;\n  transition: opacity var(--tt-transition-slow) var(--tt-animation-easing-smooth);\n}\n\n.preview-progress {\n  position: absolute;\n  z-index: 4;\n  left: 0;\n  bottom: 0;\n  width: var(--w, 0);\n  height: 0.25rem;\n  background-color: #1ec95b;\n  transition: height var(--tt-transition-medium) var(--tt-animation-easing-smooth);\n}\n\n:host(:not(:hover)) .preview {\n  opacity: 0;\n}\n:host(:not(:hover)) .preview-progress {\n  height: 0;\n}\n\n.selection-overlay {\n  z-index: 5;\n  display: grid;\n  place-items: center;\n  transition: background-color 250ms;\n}\n.selection-overlay:not(.selectable) {\n  pointer-events: none;\n}\n.selection-overlay .selection-symbol {\n  width: 40px;\n  height: 40px;\n  display: grid;\n  place-self: center;\n  place-items: center;\n  box-shadow: var(--tt-shadow-large);\n  border-radius: var(--tt-border-radius-circle);\n  background-color: #1ec95b;\n  color: white;\n  transition: transform 250ms;\n}\n.selection-overlay.selected {\n  background-color: rgba(30, 201, 91, 0.16);\n}\n.selection-overlay:not(.selected) .selection-symbol {\n  transform: scale(0);\n}\n\n:host(:hover) .selection-overlay.selectable {\n  background-color: rgba(30, 201, 91, 0.16);\n}\n:host(:hover) .selection-overlay.selectable.selected {\n  background-color: rgba(30, 201, 91, 0.24);\n}\n\n:host(:where(:hover, :not([hoverable]), [hoverable=\"false\"])) .gradient {\n  background-position-y: 0;\n}\n\n.link {\n  z-index: 6;\n  position: absolute;\n  inset: 0;\n}\n.link:focus-visible {\n  background-color: rgba(30, 201, 91, 0.16);\n  outline: 2px solid black;\n  outline-offset: 2px;\n}\n\n.info {\n  z-index: 4;\n  inset: 0;\n  color: white;\n  font-family: var(--tt-font-default);\n  font-size: 11px;\n  line-height: 15px;\n  overflow: hidden;\n}\n.info > * {\n  position: absolute;\n}\n.info .label {\n  height: 1.8em;\n  background: rgba(0, 0, 0, 0.4);\n  padding-left: var(--tt-spacing-xs);\n  padding-right: var(--tt-spacing-xs);\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  font-weight: 400;\n  border-radius: var(--tt-border-radius-md);\n  display: flex;\n  align-items: center;\n  gap: 5px;\n  font-size: 12px;\n  line-height: 1.8em;\n  font-feature-settings: \"tnum\";\n}\n.info .label span {\n  display: inline-block;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.info .header {\n  display: flex;\n  inset: var(--tt-spacing-xs) var(--tt-spacing-xs) auto;\n  justify-content: space-between;\n  gap: var(--tt-spacing-2xs);\n}\n.info .header .label {\n  background: rgba(0, 0, 0, 0.24);\n}\n.info .header .label.metrics:not(:has(span)) {\n  padding-inline: 0.375rem;\n}\n.info .header .label.metrics .label--prefix {\n  font-size: 1rem;\n}\n.info .header .label:not(.close, .add, :first-of-type):last-of-type {\n  margin-left: auto;\n}\n.info .header .add,\n.info .header .close {\n  height: 21.6px;\n  width: 21.6px;\n  padding: 0;\n  appearance: none;\n  border: none;\n  color: white;\n  background-color: black;\n  font-size: 1.25rem;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  cursor: pointer;\n}\n.info .header .add:where(:hover, :focus-visible) {\n  outline: none;\n  background-color: #1ec95b;\n}\n.info .header .close:where(:hover, :focus-visible) {\n  outline: none;\n  background-color: #fa4c4c;\n}\n.info .footer {\n  container-type: inline-size;\n  display: flex;\n  inset: auto 0 0;\n  padding: 5% 8px 8px 8px;\n  flex-direction: column;\n  gap: var(--tt-spacing-2xs);\n  background: linear-gradient(360deg, rgba(0, 0, 0, 0.5) 20%, rgba(0, 0, 0, 0) 100%);\n}\n.info .footer * {\n  display: flex;\n}\n.info .footer .label {\n  background: rgba(0, 0, 0, 0.4);\n  padding-left: var(--tt-spacing-2xs);\n  padding-right: var(--tt-spacing-2xs);\n}\n.info .footer .avatars {\n  isolation: isolate;\n}\n.info .footer .avatars.empty {\n  display: none;\n}\n.info .footer .text {\n  width: 100%;\n  flex-direction: column;\n  text-overflow: ellipsis;\n  gap: var(--tt-spacing-2xs);\n  overflow: hidden;\n}\n.info .footer .title {\n  font-weight: var(--tt-font-weight-medium);\n  font-size: var(--tt-font-size-sm);\n  line-height: var(--tt-font-size-md);\n  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.8);\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  overflow: hidden;\n  color: #ffffff;\n}\n.info .footer .placeholder {\n  gap: 1em;\n  align-items: flex-end;\n  justify-content: space-between;\n}\n.info .footer .labels {\n  gap: var(--tt-spacing-2xs);\n}\n.info .footer .timing {\n  flex-shrink: 0;\n  max-width: 60%;\n}\n.info .footer .timeline {\n  display: block;\n}\n.info .footer .timeline slot {\n  width: 100%;\n}\n.info .footer .timeline.empty {\n  display: none;\n}\n\n.labels .label.state {\n  flex-shrink: 1;\n}\n.labels .label.state::before {\n  content: \"\";\n  width: 6px;\n  height: 6px;\n  min-width: 6px;\n  border-radius: var(--tt-border-radius-circle);\n  box-shadow: 0 0 4px 0 rgba(255, 255, 255, 0.25);\n}\n.labels .label.category {\n  flex-shrink: 1;\n}\n\n::slotted(tt-avatar) {\n  --border: 2px solid white;\n  transition: opacity 250ms ease-in-out;\n  opacity: 1;\n  --group: 0.25;\n}\n\n::slotted(tt-avatar:nth-of-type(n + 6)) {\n  display: none;\n}\n\n::slotted(tt-avatar:nth-of-type(1)) {\n  z-index: 105;\n}\n\n::slotted(tt-avatar:nth-of-type(2)) {\n  z-index: 104;\n}\n\n::slotted(tt-avatar:nth-of-type(3)) {\n  z-index: 103;\n}\n\n::slotted(tt-avatar:nth-of-type(4)) {\n  z-index: 102;\n}\n\n::slotted(tt-avatar:nth-of-type(5)) {\n  z-index: 101;\n}\n\n@container (max-width: 270px) {\n  .footer .avatars {\n    zoom: 0.75;\n  }\n}\n@container (max-width: 250px) {\n  .footer .avatars {\n    display: none;\n  }\n}\n[data-state=waiting] .state::before {\n  background-color: #fac64c;\n}\n\n[data-state=unpublished] .state::before {\n  background-color: #fac64c;\n}\n\n[data-state=draft] .state::before {\n  background-color: #fac64c;\n}\n\n[data-state=scheduled] .state::before {\n  background-color: #0b4a21;\n}\n\n[data-state=live] .state::before {\n  background-color: #fa4c4c;\n}\n\n[data-state=published] .state::before {\n  background-color: #1ec95b;\n}\n\n[data-state=ondemand] .state::before {\n  background-color: #0b4a21;\n}\n\n[data-state=upcoming] .state::before {\n  background-color: #f48630;\n}\n\n[data-state=new] .state::before {\n  background-color: #1ec95b;\n}\n\n[data-state=cancelled] .state::before {\n  background-color: black;\n}\n\n[data-state=past] .state::before {\n  background-color: #cccccc;\n}\n\n[data-state=waitingroom] .state::before {\n  background-color: #c380fe;\n}\n\n[data-state=secret] .state::before {\n  background-color: #c380fe;\n}\n\n[data-state=private] .state::before {\n  background-color: #c380fe;\n}\n\n[data-state=template] .state::before {\n  background-color: #4d86f9;\n}\n\n[data-state=waiting] .header .state.highlight {\n  background-color: #fac64c;\n}\n\n[data-state=unpublished] .header .state.highlight {\n  background-color: #fac64c;\n}\n\n[data-state=draft] .header .state.highlight {\n  background-color: #fac64c;\n}\n\n[data-state=scheduled] .header .state.highlight {\n  background-color: #0b4a21;\n}\n\n[data-state=live] .header .state.highlight {\n  background-color: #fa4c4c;\n}\n\n[data-state=published] .header .state.highlight {\n  background-color: #1ec95b;\n}\n\n[data-state=ondemand] .header .state.highlight {\n  background-color: #0b4a21;\n}\n\n[data-state=upcoming] .header .state.highlight {\n  background-color: #f48630;\n}\n\n[data-state=new] .header .state.highlight {\n  background-color: #1ec95b;\n}\n\n[data-state=cancelled] .header .state.highlight {\n  background-color: black;\n}\n\n[data-state=past] .header .state.highlight {\n  background-color: #cccccc;\n}\n\n[data-state=waitingroom] .header .state.highlight {\n  background-color: #c380fe;\n}\n\n[data-state=secret] .header .state.highlight {\n  background-color: #c380fe;\n}\n\n[data-state=private] .header .state.highlight {\n  background-color: #c380fe;\n}\n\n[data-state=template] .header .state.highlight {\n  background-color: #4d86f9;\n}\n\n.active div {\n  border-radius: 5px;\n  border-style: solid;\n  border-width: 2px;\n  border-color: rgba(30, 201, 91, 0.8);\n  position: absolute;\n  inset: -4px;\n}\n\n@keyframes glow {\n  0% {\n    box-shadow: 0 0 0 0 var(--beam-color);\n    opacity: 1;\n  }\n  100% {\n    box-shadow: 0 0 0 0.25rem var(--beam-color);\n    opacity: 0.6;\n  }\n}\n.beam div {\n  border-radius: 4px;\n  animation: glow 2s alternate infinite;\n  position: absolute;\n  inset: -0.2rem;\n}\n.beam[data-state=waiting] div {\n  --beam-color: #fac64c;\n}\n.beam[data-state=unpublished] div {\n  --beam-color: #fac64c;\n}\n.beam[data-state=draft] div {\n  --beam-color: #fac64c;\n}\n.beam[data-state=scheduled] div {\n  --beam-color: #1ec95b;\n}\n.beam[data-state=live] div {\n  --beam-color: #fa4c4c;\n}\n.beam[data-state=published] div {\n  --beam-color: #1ec95b;\n}\n.beam[data-state=ondemand] div {\n  --beam-color: #1ec95b;\n}\n.beam[data-state=upcoming] div {\n  --beam-color: #f48630;\n}\n.beam[data-state=new] div {\n  --beam-color: #1ec95b;\n}\n.beam[data-state=cancelled] div {\n  --beam-color: black;\n}\n.beam[data-state=past] div {\n  --beam-color: #cccccc;\n}\n.beam[data-state=waitingroom] div {\n  --beam-color: #c380fe;\n}\n.beam[data-state=secret] div {\n  --beam-color: #c380fe;\n}\n.beam[data-state=private] div {\n  --beam-color: #c380fe;\n}\n.beam[data-state=template] div {\n  --beam-color: #4d86f9;\n}\n\n@media (prefers-reduced-motion) {\n  .beam div {\n    animation: glow 1ms;\n    animation-fill-mode: forwards;\n  }\n}\n/* Handle .hoverable attribute */\n:host(:where([hoverable=\"true\"], [hoverable]:not([hoverable=\"false\"]))) ::slotted(tt-avatar) {\n  --group: 1;\n  opacity: 0;\n}\n\n:host(:hover:where([hoverable=\"true\"], [hoverable]:not([hoverable=\"false\"]))) ::slotted(tt-avatar) {\n  opacity: 1;\n  --group: 0.25;\n}";

/**
 * @slot avatars - Avatars, usually for webinar speakers
 * @slot timeline - Timeline, usually for a Personal video
 */
const shortTimeHumanizer = humanizeDuration.humanizer({
  round: true,
  largest: 1,
  language: 'shortEn',
  spacer: '',
  conjunction: ' ',
  units: ['y', 'mo', 'w', 'd', 'h', 'm', 's'],
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});
const longTimeHumanizer = humanizeDuration.humanizer({
  round: true,
  largest: 1,
  language: 'shortEn',
  spacer: '',
  conjunction: ', ',
  units: ['y', 'mo', 'w', 'd', 'h', 'm'],
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});
const veryLongTimeHumanizer = humanizeDuration.humanizer({
  round: true,
  largest: 1,
  language: 'shortEn',
  spacer: '',
  conjunction: ', ',
  units: ['y', 'mo', 'w', 'd', 'h', 'm'],
  languages: {
    shortEn: {
      y: () => 'years',
      mo: () => 'months',
      w: () => 'weeks',
      d: () => 'days',
      h: () => 'hhours',
      m: () => 'minutes',
      s: () => 'seconds',
      ms: () => 'ms',
    },
  },
});
const AriaTypes = {
  video: 'Video',
  webinar: 'Webinar',
  live: 'Live',
  liveevent: 'Live Event',
  event: 'Digital Event',
  personal: 'Personal Video',
  library: 'Video Library video',
  liveseries: 'Webinars',
};
const VideoCard = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.ttSelectionChange = createEvent(this, "tt-selection-change", 7);
    this.ttAdd = createEvent(this, "tt-add", 7);
    this.ttRemove = createEvent(this, "tt-remove", 7);
    this.avatarsCount = 0;
    this.hasTimeline = false;
    this.previewTime = 0;
    this.timerLabel = '';
    this.videoId = undefined;
    this.type = 'video';
    this.displayType = false;
    this.displayState = true;
    this.thumbnail = undefined;
    this.preview = undefined;
    this.duration = undefined;
    this.href = undefined;
    this.target = '_self';
    this.state = 'published';
    this.metricsLabel = undefined;
    this.stateLabel = undefined;
    this.stateLabelTimerTarget = undefined;
    this.stateLabelTimerDirection = 'down';
    this.metricsIcon = 'play';
    this.metricsIconLibrary = '23';
    this.timing = undefined;
    this.cardTitle = undefined;
    this.category = undefined;
    this.dateTime = undefined;
    this.selectable = undefined;
    this.hoverable = undefined;
    this.selected = undefined;
    this.active = undefined;
    this.beam = false;
    this.showAddButton = false;
    this.showRemoveButton = false;
  }
  connectedCallback() {
    this.handleDefaultSlotChange = this.handleDefaultSlotChange.bind(this);
    this.handleAvatarSlotChange = this.handleAvatarSlotChange.bind(this);
    this.handleTimelineSlotChange = this.handleTimelineSlotChange.bind(this);
    this.getTopRightLabelType = this.getTopRightLabelType.bind(this);
    this.onMove = this.onMove.bind(this);
    this.getPreviewFrameOffset = this.getPreviewFrameOffset.bind(this);
    this.getTimingLabel = this.getTimingLabel.bind(this);
    this.getDateTimeLabel = this.getDateTimeLabel.bind(this);
    this.getTimerLabel = this.getTimerLabel.bind(this);
    this.getStateLabel = this.getStateLabel.bind(this);
    this.getTopRightLabelType = this.getTopRightLabelType.bind(this);
    this.handleSelectionChangeClick = this.handleSelectionChangeClick.bind(this);
  }
  handleDefaultSlotChange(event) {
    const avatars = extractElementsFromSlot(event.target, 'tt-avatar');
    avatars.forEach((avatar) => {
      avatar.slot = 'avatars';
    });
    const timeline = extractElementFromSlot(event.target, 'tt-timeline');
    if (timeline) {
      timeline.slot = 'timeline';
    }
  }
  handleAvatarSlotChange(event) {
    const avatars = extractElementsFromSlot(event.target, 'tt-avatar');
    this.avatarsCount = avatars.length;
    avatars.forEach((node) => {
      node.setAttribute('size', 'md');
    });
  }
  handleTimelineSlotChange(event) {
    const timeline = extractElementFromSlot(event.target, 'tt-timeline');
    this.hasTimeline = !!timeline;
  }
  handleSelectionChangeClick() {
    if (this.selectable) {
      this.selected = !this.selected;
      this.ttSelectionChange.emit({
        videoId: this.videoId,
        selected: this.selected,
      });
    }
  }
  static getDateTime(dateTime) {
    const formatter = new Intl.DateTimeFormat('default', {
      dateStyle: 'medium',
    });
    switch (typeof dateTime) {
      case 'string': {
        const integer = Number.parseInt(dateTime);
        if (Number.isNaN(integer))
          return dateTime;
        return formatter.format(new Date(integer * 1000));
      }
      case 'number':
        return formatter.format(new Date(dateTime));
      default:
        return formatter.format(dateTime);
    }
  }
  onMove(event) {
    this.previewTime = event.offsetX / this.element.offsetWidth; // TODO: throttle
  }
  getPreviewFrameOffset() {
    var _a;
    const duration = (_a = this.duration) !== null && _a !== void 0 ? _a : 1;
    const frameCount = Math.ceil(duration / Math.ceil(duration / 200)) - 1;
    return Math.floor(this.previewTime * frameCount) / frameCount;
  }
  getTimingLabel() {
    if (!this.timing)
      return null;
    return (h("div", { class: "label timing", "aria-label": "Duration of the video" }, h("span", null, typeof this.timing === 'number' ? h("tt-format-duration", { value: Math.round(this.timing) }) : this.timing)));
  }
  getDateTimeLabel() {
    if (!this.dateTime)
      return null;
    return (h("div", { class: "label date-time", "aria-label": `This ${AriaTypes[this.type]} was created at` }, h("span", null, VideoCard.getDateTime(this.dateTime))));
  }
  getTimerLabel() {
    if (!this.stateLabelTimerTarget)
      return null;
    if (typeof this.stateLabelTimerTarget === 'number') {
      this.stateLabelTimerTarget = new Date(this.stateLabelTimerTarget * 1000);
    }
    const update = function () {
      const diff = this.stateLabelTimerDirection === 'down'
        ? this.stateLabelTimerTarget.getTime() - new Date().getTime()
        : new Date().getTime() - this.stateLabelTimerTarget.getTime();
      if (diff > 0) {
        const days = diff / 86400000;
        const formater = days > 60 ? veryLongTimeHumanizer : days > 1 ? longTimeHumanizer : shortTimeHumanizer;
        this.timerLabel = formater(diff);
      }
      else {
        this.timerLabel = 'Now';
      }
    }.bind(this);
    if (!this.timerInterval) {
      this.timerInterval = window.setInterval(update, 300);
      update();
    }
    return (h("div", { class: "label state timer highlight" }, h("span", null, this.stateLabel || toTitleCase(this.state === 'waitingroom' ? 'Waiting Room' : this.state), " ", this.timerLabel)));
  }
  getStateLabel() {
    if (!this.displayState)
      return h("div", null);
    return (h("div", { class: {
        label: true,
        state: true,
        highlight: this.state === 'new' || this.state === 'live' || this.state === 'waitingroom',
      }, "data-state": this.state }, h("span", null, this.stateLabel || toTitleCase(this.state === 'waitingroom' ? 'Waiting Room' : this.state))));
  }
  getTopRightLabelType() {
    if (this.stateLabelTimerTarget) {
      return 'timer';
    }
    if (this.state === 'new') {
      return 'state';
    }
    if (this.type === 'video') {
      return 'timing';
    }
    switch (this.state) {
      case 'published':
        if (this.type === 'personal') {
          return this.timing ? 'timing' : 'state';
        }
        else {
          return this.dateTime ? 'date-time' : 'state';
        }
      case 'draft':
      case 'upcoming':
      case 'unpublished':
      case 'scheduled':
      case 'cancelled':
      case 'waiting':
      case 'secret':
      case 'private':
      case 'live':
      default:
        return 'state';
    }
  }
  render() {
    var _a, _b;
    const thumbnails = Array.isArray(this.thumbnail) ? this.thumbnail : (_b = (_a = this.thumbnail) === null || _a === void 0 ? void 0 : _a.split(',')) !== null && _b !== void 0 ? _b : [];
    return (h(Host, { onMouseEnter: () => {
        window.addEventListener('mousemove', this.onMove);
      }, onMouseLeave: () => {
        window.removeEventListener('mousemove', this.onMove);
      }, onClick: this.handleSelectionChangeClick, "aria-selected": this.selected, "aria-label": `${AriaTypes[this.type]} card ${this.cardTitle ? `with title "${this.cardTitle}"` : ', untitled'}` }, h(MediaCardContainer, null, ['webinarseries', 'webinarevent'].includes(this.type) && (h("div", { class: "thumbnail-stack", style: thumbnails[1]
        ? {
          'background-image': `url(${thumbnails[1]})`,
        }
        : undefined })), h("div", { class: "image", role: "img", "aria-hidden": "true", "aria-label": `${AriaTypes[this.type]} thumbnail`, style: thumbnails[0] ? { 'background-image': `url(${thumbnails[0]})` } : undefined }, this.preview && !this.selectable && (h("div", { class: "preview", style: {
        'background-image': `url(${this.preview})`,
        '--offset-y': `${this.getPreviewFrameOffset() * 100}%`,
      } }))), this.preview && !this.selectable && (h("div", { class: "preview-progress", style: {
        '--w': `${this.previewTime * 100}%`,
      } })), h("div", { class: {
        'selectable': this.selectable,
        'selected': this.selected,
        'selection-overlay': true,
      } }, h("div", { class: "selection-symbol" }, h("tt-icon", { "aria-hidden": "true", library: "one", name: "check" }))), h("div", { class: "info", "data-state": this.state }, h("div", { class: "header" }, (this.metricsLabel || this.metricsIcon) && (h("div", { class: "label metrics", "aria-label": `Metrics value preceded by a ${this.metricsIcon} icon` }, this.metricsIcon && (h("tt-icon", { class: "label--prefix", "aria-hidden": "true", name: this.metricsIcon, library: this.metricsIconLibrary })), this.metricsLabel && h("span", null, this.metricsLabel))), h("div", null), {
      'timing': this.getTimingLabel,
      'date-time': this.getDateTimeLabel,
      'state': this.getStateLabel,
      'timer': this.getTimerLabel,
    }[this.getTopRightLabelType()](), h("slot", { name: "label" }), this.showAddButton && (h("button", { class: "label add", onClick: () => this.ttAdd.emit() }, h("tt-icon", { library: "one", name: "plus" }))), this.showRemoveButton && (h("button", { class: "label close", onClick: () => this.ttRemove.emit() }, h("tt-icon", { library: "one", name: "close" })))), h("div", { class: "footer" }, h("div", { "aria-hidden": this.avatarsCount === 0, "aria-label": `This ${AriaTypes[this.type]} card has ${this.avatarsCount} people associated with it`, class: { empty: this.avatarsCount === 0, avatars: true } }, h("slot", { name: "avatars", onSlotchange: this.handleAvatarSlotChange })), h("div", { class: "placeholder" }, h("div", { class: "text" }, h("div", { class: "labels" }, this.type && this.displayType && (h("div", { class: "label type" }, h("span", null, toTitleCase(this.type)))), this.category && (h("div", { "aria-label": `${AriaTypes[this.type]} category`, class: "label category" }, h("span", null, this.category))), this.displayState &&
      this.getTopRightLabelType() !== 'state' &&
      this.getTopRightLabelType() !== 'timer' && (h("div", { "aria-label": `State of the ${AriaTypes[this.type]}`, class: "label state" }, h("span", null, toTitleCase(this.state))))), this.cardTitle && (h("div", { class: "title", "aria-label": `${AriaTypes[this.type]} car title` }, this.cardTitle)), ['webinar', 'webinarseries', 'webinarevent'].includes(this.type) && this.dateTime && (h("div", { class: "data date-time" }, this.dateTime)))), h("div", { class: { empty: !this.hasTimeline, timeline: true } }, h("slot", { name: "timeline", onSlotchange: this.handleTimelineSlotChange })))), this.beam && (h("div", { class: "beam", "data-state": this.state }, h("div", null))), this.active && (h("div", { class: "active" }, h("div", null))), this.href && !this.selectable && (h("a", { class: "link", href: this.href, target: this.target, "aria-label": `Go to ${AriaTypes[this.type]}` }))), h("slot", { onSlotchange: this.handleDefaultSlotChange })));
  }
  get element() { return this; }
  static get style() { return videoCardCss; }
}, [1, "tt-video-card", {
    "videoId": [1, "video-id"],
    "type": [1],
    "displayType": [4, "display-type"],
    "displayState": [4, "display-state"],
    "thumbnail": [1],
    "preview": [1],
    "duration": [2],
    "href": [1],
    "target": [1],
    "state": [1],
    "metricsLabel": [1, "metrics-label"],
    "stateLabel": [1, "state-label"],
    "stateLabelTimerTarget": [2, "state-label-timer-target"],
    "stateLabelTimerDirection": [1, "state-label-timer-direction"],
    "metricsIcon": [1, "metrics-icon"],
    "metricsIconLibrary": [1, "metrics-icon-library"],
    "timing": [8],
    "cardTitle": [1, "card-title"],
    "category": [1],
    "dateTime": [8, "date-time"],
    "selectable": [516],
    "hoverable": [516],
    "selected": [1540],
    "active": [516],
    "beam": [4],
    "showAddButton": [4, "show-add-button"],
    "showRemoveButton": [4, "show-remove-button"],
    "avatarsCount": [32],
    "hasTimeline": [32],
    "previewTime": [32],
    "timerLabel": [32]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-video-card", "tt-format-duration", "tt-icon"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-video-card":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, VideoCard);
      }
      break;
    case "tt-format-duration":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "tt-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const TtVideoCard = VideoCard;
const defineCustomElement = defineCustomElement$1;

export { TtVideoCard, defineCustomElement };
